import { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Landmark,
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  Calendar,
  TrendingUp,
  TrendingDown,
  Filter,
  X,
  FileUp
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Transaction categories for auto-classification
const CATEGORY_RULES: { pattern: RegExp; category: string; subcategory?: string; isBusiness: boolean }[] = [
  // FUEL - Business
  { pattern: /LOVES|PILOT|FLYING J|TA PETRO|PETRO STOPPING|SAPP BROS|KWICK TRIP|KWIK TRIP|CASEY|SHEETZ|WAWA|RACETRAC|QT |QUIKTRIP|BUCKEE|SHELL|EXXON|CHEVRON|BP |MARATHON|SPEEDWAY|CIRCLE K|7-ELEVEN.*GAS|FUEL|DIESEL/i, category: 'fuel', isBusiness: true },
  
  // TRUCK STOPS (likely fuel or food on road)
  { pattern: /TRUCK STOP|TRAVEL CENTER|TRAVEL PLAZA|REST STOP/i, category: 'fuel', isBusiness: true },
  
  // INSURANCE - Business
  { pattern: /TRANSPORTATION INSUROR|TRUCK INSURANCE|COMMERCIAL INS|PROGRESSIVE COMM|GREAT WEST|NATIONAL INDEM/i, category: 'insurance', isBusiness: true },
  { pattern: /ALLSTATE|STATE FARM|GEICO|PROGRESSIVE|FARMERS INS/i, category: 'insurance', isBusiness: true },
  
  // SETTLEMENTS/INCOME - Business
  { pattern: /SMARTPAY|DYNAMIC|HORIZON TRANS|BENNETT|QUALITY DRIVE|COLDIRON|AUTO DRIVEAWAY|EFS PAYMENT|COMDATA|FACTORING/i, category: 'income', isBusiness: true },
  
  // TRUCK MAINTENANCE - Business
  { pattern: /AUTOZONE|O'REILLY|NAPA AUTO|ADVANCE AUTO|TRUCK PARTS|FREIGHTLINER|PETERBILT|KENWORTH|INTERNATIONAL TRUCK|SPEEDCO|BLUE BEACON|TRUCK WASH/i, category: 'maintenance', isBusiness: true },
  
  // TOLLS - Business
  { pattern: /EZPASS|E-ZPASS|SUNPASS|IPASS|FASTRAK|TOLL|TURNPIKE|BRIDGE AUTH|TUNNEL AUTH/i, category: 'tolls', isBusiness: true },
  
  // SCALES/PERMITS - Business
  { pattern: /CAT SCALE|SCALE|PERMIT|DOT |IFTA|IRP|UCR REG/i, category: 'permits', isBusiness: true },
  
  // PHONE - Potentially Business
  { pattern: /VERIZON|AT&T|T-MOBILE|SPRINT|CRICKET|METRO PCS|VISIBLE|MINT MOBILE/i, category: 'phone', isBusiness: true },
  
  // MEALS ON ROAD (truck stops, fast food) - Business
  { pattern: /IRON SKILLET|DENNY'S|WAFFLE HOUSE|CRACKER BARREL|SUBWAY.*TRAVEL|MCDONALD.*TRAVEL|WENDY.*TRAVEL/i, category: 'meals', subcategory: 'road', isBusiness: true },
  
  // LODGING - Business
  { pattern: /MOTEL 6|SUPER 8|DAYS INN|LA QUINTA|RED ROOF|ECONO LODGE|QUALITY INN|COMFORT INN|HOLIDAY INN|BEST WESTERN|HOTEL|MOTEL|INN |LODGE/i, category: 'lodging', isBusiness: true },
  
  // PARKING - Business
  { pattern: /PARKING|TRUCK PARK|SECURE PARKING/i, category: 'parking', isBusiness: true },
  
  // ---- PERSONAL CATEGORIES ----
  
  // STREAMING & SUBSCRIPTIONS - Personal
  { pattern: /NETFLIX|HULU|DISNEY\+|HBO|SPOTIFY|APPLE MUSIC|YOUTUBE PREMIUM|AMAZON PRIME|PARAMOUNT|APPLE SERVICES|ANCESTRY|BULENOX|TAKE PROFIT TRADER|GOOGLE STORAGE/i, category: 'streaming', isBusiness: false },
  
  // RENT/HOUSING - Personal
  { pattern: /ABBEY|APARTMENT|RENT |LEASE|PROPERTY MGMT|RESIDENT DIR/i, category: 'rent', isBusiness: false },
  
  // INVESTMENTS - Personal
  { pattern: /ROBINHOOD|FIDELITY|SCHWAB|ETRADE|WEBULL|COINBASE|CRYPTO/i, category: 'investment', isBusiness: false },
  
  // RIDESHARE - Usually Personal (could be business if for work travel)
  { pattern: /UBER TECHNOLOGIES|LYFT|UBER (?!EATS)/i, category: 'rideshare', isBusiness: null as any },
  
  // FOOD DELIVERY (usually personal) - Personal
  { pattern: /UBER EATS|DOORDASH|GRUBHUB|POSTMATES|INSTACART/i, category: 'food_delivery', isBusiness: false },
  
  // GENERAL RESTAURANTS (need trip context) - Unknown
  { pattern: /MCDONALD|WENDY|BURGER KING|TACO BELL|CHICK-FIL|SUBWAY|CHIPOTLE|PANERA|STARBUCKS|DUNKIN/i, category: 'meals', isBusiness: null as any }, // null = needs trip context
  
  // UTILITIES - Personal (unless home office)
  { pattern: /STREAM ENERGY|ELECTRIC|GAS COMPANY|WATER UTIL|INTERNET|COMCAST|SPECTRUM|COX COMM/i, category: 'utilities', isBusiness: false },
  
  // LOAN PAYMENTS - Mixed
  { pattern: /ONEMAIN|CAR PAYMENT|AUTO LOAN|TRUCK PAYMENT/i, category: 'loan', isBusiness: null as any }, // Could be truck loan = business
  
  // CREDIT CARD PAYMENTS - Transfer (ignore)
  { pattern: /CAPITAL ONE.*PMT|CREDIT ONE|SYNCHRONY|DISCOVER.*PMT|CHASE.*PMT|AMEX.*PMT|CC PYMT/i, category: 'transfer', isBusiness: false },
  
  // TRANSFERS - Ignore
  { pattern: /ZELLE|CASH APP|VENMO|PAYPAL|TRANSFER|P2P|ACH.*SULLI/i, category: 'transfer', isBusiness: false },
];

interface BankTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  balance?: number;
  category: string;
  isBusiness: boolean | null; // null = needs review
  isIncome: boolean;
  selected: boolean;
  onTrip?: boolean; // Will be set when cross-referencing with settlements
  bankSource?: 'NFCU' | 'Capital One' | 'Capital One CC' | 'GPA' | 'PayPal' | 'Other'; // Which bank account
  paymentCompany?: string; // For income: which transport company paid
  location?: string; // City, State extracted from merchant description
  isSuspiciousIncome?: boolean; // Warning: looks like personal transfer, not real settlement
  suspiciousReason?: string; // Why it's flagged as suspicious
}

// Detect if an "income" transaction looks like a personal transfer (NOT a real settlement)
const detectSuspiciousIncome = (description: string, amount: number): { isSuspicious: boolean; reason?: string } => {
  const descUpper = description.toUpperCase();
  
  // Personal P2P transfers - NOT real income
  if (/P2P|PERSON TO PERSON|FROM.*SULLIVAN|FROM.*DOUGLAS|SULLI.*P2P/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'Looks like P2P transfer from yourself' };
  }
  
  // Cash deposits - NOT settlement income
  if (/CASH DEPOSIT|DEPOSIT AT BRANCH|BRANCH DEPOSIT|ATM DEPOSIT/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'Cash deposit - not a settlement payment' };
  }
  
  // Money received from self
  if (/MONEY RECEIVED FROM|RECEIVED FROM.*DOUG|RECEIVED FROM.*SULLIVAN/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'Transfer from yourself - not settlement income' };
  }
  
  // Zelle/Venmo/CashApp - Usually personal
  if (/ZELLE|VENMO|CASH APP|CASHAPP/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'P2P app transfer - likely personal' };
  }
  
  // Transfer between own accounts
  if (/TRANSFER FROM.*CHECKING|TRANSFER FROM.*SAVINGS|INTERNAL TRANSFER|ACCOUNT TRANSFER/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'Transfer between your own accounts' };
  }
  
  // Generic "deposit" without company name
  if (/^DEPOSIT$|^DIRECT DEPOSIT$|^ACH DEPOSIT$/i.test(descUpper.trim())) {
    return { isSuspicious: true, reason: 'Generic deposit - verify source company' };
  }
  
  // Round amounts that look like personal transfers (not settlements)
  const roundAmounts = [100, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1000];
  if (roundAmounts.includes(amount) && !/SMARTPAY|EFS|COMDATA|DYNAMIC|BENNETT|QUALITY|HORIZON/i.test(descUpper)) {
    return { isSuspicious: true, reason: 'Round amount without settlement company name' };
  }
  
  return { isSuspicious: false };
};

// Transport companies for income tracking (companies you've worked for)
const TRANSPORT_COMPANIES = [
  { value: 'Bennett', label: 'Bennett Motor Express', color: 'blue' },
  { value: 'Auto Driveaway', label: 'Auto Driveaway', color: 'purple' },
  { value: 'Quality Driveaway', label: 'Quality Driveaway', color: 'green' },
  { value: 'Coldiron', label: 'Coldiron Logistics', color: 'orange' },
  { value: 'Dynamic', label: 'Dynamic Transport', color: 'cyan' },
  { value: 'Horizon', label: 'Horizon Transport', color: 'pink' },
  { value: 'Lakefront', label: 'Lakefront Lines (Hotshot)', color: 'teal' },
  { value: 'GPA Transfer', label: 'GPA Business Transfer', color: 'indigo' },
  { value: 'Other', label: 'Other / Unknown', color: 'gray' },
];

export default function ImportBankStatement() {
  const { addExpense, expenses } = useExpenses();
  const [statementText, setStatementText] = useState('');
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number; skipped: number; incomeImported?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'business' | 'personal' | 'review' | 'income' | 'on_trip' | 'away' | 'at_home' | 'suspicious'>('all');
  const [filterYear, setFilterYear] = useState<number | 'all'>('all');
  
  // Target year for import
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);
  
  // ===== TRIP MATCHING - Auto-detect if transaction was made while OTR =====
  interface TripRange {
    startDate: string;
    endDate: string;
    company: string;
    origin: string;
    destination: string;
    miles: number;
  }

  // Build trip ranges from settlements
  const tripRanges = useMemo((): TripRange[] => {
    const settlements = expenses.filter(e => e.category === 'settlements');
    
    // Estimate trip duration based on miles (avg 500 miles/day for OTR)
    const estimateTripDays = (miles: number): number => {
      if (miles <= 0) return 1;
      return Math.max(1, Math.ceil(miles / 500));
    };

    const ranges = settlements.map(s => {
      const miles = s.miles || Math.round((s.amount || 0) / 1.75);
      const daysOnRoad = estimateTripDays(miles);
      
      const endDate = new Date(s.date);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - daysOnRoad + 1);
      
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: s.date,
        company: s.company || 'Unknown',
        origin: s.origin || '',
        destination: s.destination || '',
        miles
      };
    });
    
    // Merge overlapping/adjacent ranges (gaps <= 5 days = still OTR, didn't go home)
    const sorted = ranges.sort((a, b) => a.startDate.localeCompare(b.startDate));
    const merged: TripRange[] = [];
    
    for (const range of sorted) {
      if (merged.length === 0) {
        merged.push({ ...range });
        continue;
      }
      
      const last = merged[merged.length - 1];
      const lastEnd = new Date(last.endDate);
      const thisStart = new Date(range.startDate);
      const daysBetween = Math.floor((thisStart.getTime() - lastEnd.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysBetween <= 5) {
        // Extend the range - you were still OTR
        if (range.endDate > last.endDate) {
          last.endDate = range.endDate;
        }
        last.miles += range.miles;
        if (range.destination) last.destination = range.destination;
      } else {
        merged.push({ ...range });
      }
    }
    
    return merged;
  }, [expenses]);

  // Check if a transaction date falls within a trip
  const findTripForDate = (dateStr: string): TripRange | null => {
    for (const range of tripRanges) {
      if (dateStr >= range.startDate && dateStr <= range.endDate) {
        return range;
      }
    }
    return null;
  };

  // Quick check - returns true/false
  const isOnTrip = (dateStr: string): boolean => {
    return findTripForDate(dateStr) !== null;
  };

  // Location-based OTR detection - if purchase was made outside home state, likely OTR
  // User's home: Katy, TX
  const HOME_STATE = 'TX';
  const HOME_CITIES = ['KATY', 'HOUSTON', 'SUGAR LAND', 'RICHMOND', 'ROSENBERG', 'FULSHEAR', 'CYPRESS'];
  
  const isLocationAwayFromHome = (location: string | undefined): { isAway: boolean; state?: string } => {
    if (!location) return { isAway: false };
    
    // Extract state from location (format: "City, ST")
    const match = location.match(/,\s*([A-Z]{2})$/);
    if (!match) return { isAway: false };
    
    const state = match[1];
    
    // If not in home state, definitely away
    if (state !== HOME_STATE) {
      return { isAway: true, state };
    }
    
    // In Texas - check if it's a home city
    const cityPart = location.replace(/,\s*[A-Z]{2}$/, '').toUpperCase().trim();
    const isHomeCity = HOME_CITIES.some(hc => cityPart.includes(hc));
    
    // If in TX but NOT a home city, could be OTR within Texas
    return { isAway: !isHomeCity, state };
  };
  
  // Check if a transaction already exists in the database
  const isDuplicate = (trans: BankTransaction): boolean => {
    const transDate = trans.date; // Format: YYYY-MM-DD
    return expenses.some(exp => {
      // Match by date and amount (within $0.02)
      // Use exp.date directly (format: YYYY-MM-DD) instead of reconstructing
      const dateMatch = exp.date === transDate;
      const amountMatch = Math.abs((exp.amount || 0) - trans.amount) < 0.02;
      
      return dateMatch && amountMatch;
    });
  };

  // Auto-categorize a transaction based on description
  const categorizeTransaction = (description: string, amount: number): { category: string; isBusiness: boolean | null } => {
    const descUpper = description.toUpperCase();
    
    // Check if it's a truck stop purchase
    const isTruckStop = /LOVES|PILOT|FLYING J|TA PETRO|PETRO STOPPING|SAPP BROS|TRAVEL STOP|TRAVEL CENTER|FJ #|TA EXPRESS|RATON TRUCK|MONTROSE TRAVEL|ALLSUPS|CORNER STORE|SHELL|CHEVRON|EXXON|BP |MARATHON|SPEEDWAY|CIRCLE K|7-ELEVEN|QUIKTRIP|QT |RACETRAC|CASEY|SHEETZ|WAWA|KWICK|KWIK/i.test(descUpper);
    
    // Small purchases at truck stops (< $20) are snacks/drinks/showers, not fuel
    // OTR drivers: these are business expenses (meals/misc on the road)
    if (isTruckStop && amount < 20) {
      return { category: 'otr_misc', isBusiness: true };
    }
    
    // Larger truck stop purchases ($20+) are likely fuel
    if (isTruckStop && amount >= 20) {
      return { category: 'fuel', isBusiness: true };
    }
    
    for (const rule of CATEGORY_RULES) {
      if (rule.pattern.test(descUpper)) {
        return { category: rule.category, isBusiness: rule.isBusiness };
      }
    }
    
    // Default: unknown, needs review
    return { category: 'unknown', isBusiness: null };
  };

  // Extract location (City, State) from merchant description
  // Only extract when we're confident - better to show nothing than wrong info
  const extractLocation = (description: string): string | undefined => {
    // Valid US state codes
    const validStates = new Set(['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']);
    
    // Common US cities (for validation and extraction)
    const knownCities = new Set(['HOUSTON','DALLAS','AUSTIN','CHICAGO','PHOENIX','PHILADELPHIA','ANTONIO','DIEGO','JOSE','JACKSONVILLE','COLUMBUS','CHARLOTTE','FRANCISCO','INDIANAPOLIS','SEATTLE','DENVER','NASHVILLE','BALTIMORE','BOSTON','PORTLAND','VEGAS','DETROIT','MEMPHIS','LOUISVILLE','MILWAUKEE','ALBUQUERQUE','TUCSON','FRESNO','SACRAMENTO','MESA','KANSAS','ATLANTA','OMAHA','RALEIGH','MIAMI','OAKLAND','MINNEAPOLIS','TULSA','CLEVELAND','WICHITA','ARLINGTON','TAMPA','ORLEANS','BAKERSFIELD','ANAHEIM','AURORA','SANTA','RIVERSIDE','CORPUS','LEXINGTON','PITTSBURGH','ANCHORAGE','STOCKTON','CINCINNATI','PAUL','TOLEDO','NEWARK','GREENSBORO','PLANO','HENDERSON','LINCOLN','BUFFALO','JERSEY','CHULA','ORLEANS','WORTH','CHANDLER','SCOTTSDALE','LAREDO','MADISON','RENO','RICHMOND','BATON','ROUGE','SPOKANE','GARLAND','HIALEAH','IRVING','CHESAPEAKE','WINSTON','SALEM','GLENDALE','NORFOLK','LUBBOCK','GILBERT','IRVING','FREMONT','BOISE','ABERDEEN','BEDFORD','GOSHEN']);
    
    // Pattern 1: "CITY, ST" with comma (most reliable)
    const commaMatch = description.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*([A-Z]{2})(?:\s|$)/);
    if (commaMatch && validStates.has(commaMatch[2])) {
      return `${commaMatch[1]}, ${commaMatch[2]}`;
    }
    
    // Pattern 2: Known city + state at end (handles run-together like "CARDALLASTX")
    // Look for known city names embedded in the string
    for (const city of knownCities) {
      const cityStatePattern = new RegExp(`(${city})([A-Z]{2})(?:\\s|$)`, 'i');
      const match = description.match(cityStatePattern);
      if (match && validStates.has(match[2].toUpperCase())) {
        // Capitalize properly
        const cityName = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        return `${cityName}, ${match[2].toUpperCase()}`;
      }
    }
    
    // Pattern 3: "CITY ST" with space, at end, proper case (reliable)
    const spacedMatch = description.match(/\s([A-Z][a-z]{3,}(?:\s+[A-Z][a-z]+)*)\s+([A-Z]{2})$/);
    if (spacedMatch && validStates.has(spacedMatch[2])) {
      return `${spacedMatch[1]}, ${spacedMatch[2]}`;
    }
    
    // Pattern 4: After store number - "#XXXX CITYSTATE" or "#XXXX*CITYSTATE"
    const storeMatch = description.match(/#\d{3,5}[\*\s]+([A-Z]{4,12})([A-Z]{2})(?:\s|$)/);
    if (storeMatch && validStates.has(storeMatch[2])) {
      const cityPart = storeMatch[1];
      // Check if it's a known city or looks like a real city
      if (knownCities.has(cityPart.toUpperCase()) || (!/^(.)\1{3,}/.test(cityPart) && cityPart.length >= 4 && cityPart.length <= 12)) {
        const cityName = cityPart.charAt(0).toUpperCase() + cityPart.slice(1).toLowerCase();
        return `${cityName}, ${storeMatch[2]}`;
      }
    }
    
    // Pattern 5: Known merchant chains with CITYSTATE at end (most conservative)
    // Only for merchants we recognize: PILOT, LOVES, FLYING J, TA, WALGREENS, CVS, etc.
    const chainMatch = description.match(/^(PILOT|LOVE|LOVES|FLYING J|TA |WALGREENS|CVS|7-ELEVEN|CIRCLE K|SHELL|CHEVRON|BP|EXXON|MARATHON)\s.*?([A-Z]{5,12})([A-Z]{2})$/i);
    if (chainMatch && validStates.has(chainMatch[3])) {
      const cityPart = chainMatch[2];
      // Check if it's a known city first
      if (knownCities.has(cityPart.toUpperCase())) {
        const cityName = cityPart.charAt(0).toUpperCase() + cityPart.slice(1).toLowerCase();
        return `${cityName}, ${chainMatch[3]}`;
      }
      // Otherwise, very conservative - must be reasonable length, no weird patterns
      if (cityPart.length >= 5 && cityPart.length <= 12 && !/\d|QPS|GPS|WGS/.test(cityPart)) {
        // Check for duplicates
        if (cityPart.length >= 10) {
          const half = Math.floor(cityPart.length / 2);
          if (cityPart.substring(0, half) === cityPart.substring(half)) {
            const cityName = cityPart.substring(0, half);
            return `${cityName.charAt(0).toUpperCase() + cityName.slice(1).toLowerCase()}, ${chainMatch[3]}`;
          }
        }
        return `${cityPart.charAt(0).toUpperCase() + cityPart.slice(1).toLowerCase()}, ${chainMatch[3]}`;
      }
    }
    
    // Don't try to parse ambiguous all-caps patterns - too many false positives
    return undefined;
  };

  // Parse Capital One 360 Checking statement format
  const parseCapitalOneStatement = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    // Extract year from statement period: "Jan 1 - Jan 31, 2020" or "STATEMENT PERIOD Jan 1 - Jan 31, 2020"
    const yearMatch = text.match(/(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s*[-–]\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},?\s*(\d{4})/i) ||
                     text.match(/statement.*?(\d{4})/i) ||
                     text.match(/(\d{4})\s*bank\s*statement/i);
    const year = yearMatch ? yearMatch[1] : new Date().getFullYear().toString();
    
    console.log('Capital One Checking: Detected year:', year);
    
    // Pattern 1: Pipe-delimited table format from PDF
    // | Jan 2 | Debit Card Purchase - WALGREENS STORE 950 BE ABERDEEN, MD US | | - $19.90 | $1,296.94 |
    const pipePattern = /\|\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*\|([^|]+)\|[^|]*\|\s*([+-])\s*\$?([\d,]+\.?\d*)\s*\|\s*\$?([\d,]+\.?\d*)\s*\|/gi;
    
    let match;
    while ((match = pipePattern.exec(text)) !== null) {
      const [, month, day, description, sign, amountStr] = match;
      
      // Skip header/summary lines
      if (description.includes('Opening Balance') || 
          description.includes('Closing Balance') ||
          description.includes('DESCRIPTION') ||
          description.includes('CATEGORY')) {
        continue;
      }
      
      const amount = parseFloat(amountStr.replace(/,/g, ''));
      if (isNaN(amount) || amount === 0) continue;
      
      const isIncome = sign === '+';
      const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
        .indexOf(month.toLowerCase()) + 1;
      
      const { category, isBusiness } = categorizeTransaction(description.trim(), amount);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: `${year}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
        description: description.trim(),
        amount,
        category,
        isBusiness: isIncome ? false : isBusiness, // Deposits aren't auto-business unless settlement
        isIncome,
        selected: !isIncome && isBusiness === true,
      });
    }
    
    console.log('Capital One Checking pipe pattern: Found', transactions.length, 'transactions');
    
    // Pattern 2: Line-based format (fallback) - no pipe delimiters
    // "Jan 2 Debit Card Purchase - WALGREENS STORE 950 BE - $19.90 $1,296.94"
    // or "Jan 2 Deposit from NFCU ACH + $100.00 $1,332.86"
    if (transactions.length === 0) {
      console.log('Trying line pattern for Capital One Checking');
      const linePattern = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(.+?)\s+([+-])\s*\$?([\d,]+\.\d{2})\s+\$?([\d,]+\.?\d*)/gi;
      
      while ((match = linePattern.exec(text)) !== null) {
        const [, month, day, description, sign, amountStr] = match;
        
        if (description.includes('Opening Balance') || 
            description.includes('Closing Balance') ||
            description.includes('DESCRIPTION') ||
            description.includes('AMOUNT') ||
            description.includes('BALANCE')) {
          continue;
        }
        
        const amount = parseFloat(amountStr.replace(/,/g, ''));
        if (isNaN(amount) || amount === 0) continue;
        
        const isIncome = sign === '+';
        const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
          .indexOf(month.toLowerCase()) + 1;
        
        const { category, isBusiness } = categorizeTransaction(description.trim(), amount);
        
        transactions.push({
          id: crypto.randomUUID(),
          date: `${year}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
          description: description.trim(),
          amount,
          category,
          isBusiness: isIncome ? false : isBusiness,
          isIncome,
          selected: !isIncome && isBusiness === true,
        });
      }
      
      console.log('Capital One Checking line pattern: Found', transactions.length, 'transactions');
    }
    
    // Pattern 3: Compact format with no +/- sign (amount/balance side by side)
    // "Jan 17 Debit Card Purchase UBER EATS 8005928996 CA 18.12 880.57"
    if (transactions.length === 0) {
      console.log('Trying compact pattern for Capital One Checking');
      const compactPattern = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+((?:Debit Card Purchase|Deposit|Withdrawal|Check Deposit|Cash Deposit|Electronic Payment|Money received)[^\d]+?)\s*([\d,]+\.\d{2})\s+([\d,]+\.\d{2})/gi;
      
      let prevBalance = 0;
      while ((match = compactPattern.exec(text)) !== null) {
        const [, month, day, description, amountStr, balanceStr] = match;
        
        const amount = parseFloat(amountStr.replace(/,/g, ''));
        const balance = parseFloat(balanceStr.replace(/,/g, ''));
        if (isNaN(amount) || amount === 0) continue;
        
        // Determine if income/expense by balance change
        const isIncome = balance > prevBalance;
        prevBalance = balance;
        
        const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
          .indexOf(month.toLowerCase()) + 1;
        
        const { category, isBusiness } = categorizeTransaction(description.trim(), amount);
        
        transactions.push({
          id: crypto.randomUUID(),
          date: `${year}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
          description: description.trim(),
          amount,
          category,
          isBusiness: isIncome ? false : isBusiness,
          isIncome,
          selected: !isIncome && isBusiness === true,
        });
      }
      
      console.log('Capital One Checking compact pattern: Found', transactions.length, 'transactions');
    }
    
    return transactions;
  };

  // Parse Capital One Credit Card statement format
  // Format: | Date | Description | Amount |
  // Example: | Nov 28 | DIRECT DEBIT PAYMENT | - $100.00 |
  // Example: | Dec 15 | PILOT #1049 SUFFOLK VA | $45.00 |
  const parseCapitalOneCreditCard = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    // Try to find year from statement period 
    // Format: "Dec. 22, 2019 - Jan. 21, 2020" or "Dec 22, 2019 - Jan 21, 2020"
    let startYear = '';
    let endYear = '';
    let startMonth = '';
    let endMonth = '';
    
    // Pattern for "Month. DD, YYYY - Month. DD, YYYY" or "Month DD, YYYY - Month DD, YYYY"
    const periodMatch = text.match(/(\w{3})\.?\s*(\d{1,2}),?\s*(\d{4})\s*[-–]\s*(\w{3})\.?\s*(\d{1,2}),?\s*(\d{4})/i);
    if (periodMatch) {
      startMonth = periodMatch[1];
      startYear = periodMatch[3];
      endMonth = periodMatch[4];
      endYear = periodMatch[6];
      console.log('Capital One CC: Found period:', startMonth, startYear, '-', endMonth, endYear);
    }
    
    // Fallback: Try "Payment Due Date Feb. 18, 2020" or similar
    if (!endYear) {
      const dueMatch = text.match(/(?:Due Date|Payment Due)[^\d]*(\w{3})\.?\s*(\d{1,2}),?\s*(\d{4})/i);
      if (dueMatch) {
        endYear = dueMatch[3];
        endMonth = dueMatch[1];
        console.log('Capital One CC: Found due date year:', endYear);
      }
    }
    
    // Fallback: Look for any 4-digit year that looks like 20XX
    if (!endYear) {
      const anyYearMatch = text.match(/20[12]\d/);
      if (anyYearMatch) {
        endYear = anyYearMatch[0];
        console.log('Capital One CC: Found any year:', endYear);
      }
    }
    
    // Default to current year if nothing found
    const year = endYear || new Date().getFullYear().toString();
    const prevYear = (parseInt(year) - 1).toString();
    
    // Determine if this is a year-boundary statement (Dec-Jan)
    const isYearBoundary = startMonth?.toLowerCase().startsWith('dec') && endMonth?.toLowerCase().startsWith('jan');
    
    console.log('Capital One CC: Using year:', year, 'Previous year:', prevYear, 'Year boundary:', isYearBoundary);
    
    // Pattern for Capital One CC table format:
    // | Nov 28 | DIRECT DEBIT PAYMENTAuthDate 28-Nov | - $100.00 |
    // | Dec 15 | PILOT #1049 SUFFOLK VA | $45.00 |
    const tablePattern = /\|\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*\|([^|]+)\|\s*(-?\s*\$?[\d,]+\.?\d*)\s*\|/gi;
    
    let match;
    while ((match = tablePattern.exec(text)) !== null) {
      const [, month, day, description, amountStr] = match;
      
      // Skip header rows, fees, interest lines
      const descClean = description.trim();
      if (descClean === 'Description' ||
          descClean.includes('Total Fees') ||
          descClean.includes('Total Interest') ||
          descClean.includes('Interest Charge on') ||
          descClean.includes('Totals Year-to-Date') ||
          descClean === 'Fees' ||
          descClean === 'Interest Charged' ||
          !amountStr.match(/\d/)) {
        continue;
      }
      
      const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
        .indexOf(month.toLowerCase()) + 1;
      
      // Determine correct year - December in a year-boundary statement uses previous year
      const transYear = (isYearBoundary && monthNum === 12) ? prevYear : year;
      
      // Parse amount - handle "- $100.00" format
      const cleanAmount = amountStr.replace(/[\s$,]/g, '');
      const isNegative = cleanAmount.startsWith('-');
      const amount = Math.abs(parseFloat(cleanAmount.replace('-', '')));
      
      if (isNaN(amount) || amount === 0) continue;
      
      // Payments (negative amounts) are credits, not expenses to track
      // But we might want to track them for reconciliation
      const isPayment = isNegative || 
                       descClean.includes('PAYMENT') ||
                       descClean.includes('CREDIT') ||
                       descClean.includes('REFUND');
      
      const { category, isBusiness } = categorizeTransaction(descClean, amount);
      const location = extractLocation(descClean);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: `${transYear}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
        description: descClean,
        amount,
        category: isPayment ? 'payment' : category,
        isBusiness: isPayment ? false : isBusiness, // Payments aren't business expenses
        isIncome: isPayment, // Treat payments as "income" (money coming back)
        selected: !isPayment && isBusiness === true,
        location,
      });
    }
    
    console.log('Capital One CC table pattern: Found', transactions.length, 'transactions');
    
    // Pattern 1: "Dec 15 Dec 16 DESCRIPTION $AMOUNT" or "Dec 15 DESCRIPTION $AMOUNT"
    // Credit card often has Trans Date, Post Date, then description
    const pattern1 = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(?:(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+)?([A-Z][A-Za-z0-9\s\*\#\-\.\/\&\']+?)\s+\$?([\d,]+\.?\d{0,2})\s*$/gim;
    
    let match1;
    while ((match1 = pattern1.exec(text)) !== null) {
      const [, transMonth, transDay, , , description, amountStr] = match1;
      
      // Skip summary lines
      if (description.includes('Previous Balance') ||
          description.includes('Payment') && description.includes('Thank') ||
          description.includes('New Balance') ||
          description.includes('Credit Limit') ||
          description.includes('Available Credit') ||
          description.includes('Minimum Payment') ||
          description.includes('Interest Charged') ||
          description.includes('Total') && !description.includes('TOTAL')) {
        continue;
      }
      
      const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
        .indexOf(transMonth.toLowerCase()) + 1;
      
      // Determine correct year - December in a year-boundary statement uses previous year
      const transYear = (isYearBoundary && monthNum === 12) ? prevYear : year;
      
      const amount = parseFloat(amountStr.replace(/,/g, ''));
      if (isNaN(amount) || amount === 0) continue;
      
      const descTrimmed = description.trim();
      const { category, isBusiness } = categorizeTransaction(descTrimmed, amount);
      const location = extractLocation(descTrimmed);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: `${transYear}-${monthNum.toString().padStart(2, '0')}-${transDay.padStart(2, '0')}`,
        description: descTrimmed,
        amount,
        category,
        isBusiness,
        isIncome: false, // Credit card purchases are expenses
        selected: isBusiness === true,
        location,
      });
    }
    
    // Pattern 2: Table format with pipes
    // | 12/15 | 12/16 | PILOT TRAVEL CTR | | $45.00 |
    const pattern2 = /\|\s*(\d{1,2})\/(\d{1,2})\s*\|[^|]*\|([^|]+)\|[^|]*\|\s*\$?([\d,]+\.?\d*)\s*\|/gi;
    
    let match2;
    while ((match2 = pattern2.exec(text)) !== null) {
      const [, month, day, description, amountStr] = match2;
      
      const amount = parseFloat(amountStr.replace(/,/g, ''));
      if (isNaN(amount) || amount === 0) continue;
      
      // Determine correct year - December in a year-boundary statement uses previous year
      const monthNum = parseInt(month);
      const transYear = (isYearBoundary && monthNum === 12) ? prevYear : year;
      
      // Skip if already found
      const dateStr = `${transYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const exists = transactions.some(t => t.date === dateStr && Math.abs(t.amount - amount) < 0.01);
      if (exists) continue;
      
      const descTrimmed = description.trim();
      const { category, isBusiness } = categorizeTransaction(descTrimmed, amount);
      const location = extractLocation(descTrimmed);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: dateStr,
        description: descTrimmed,
        amount,
        category,
        isBusiness,
        isIncome: false,
        selected: isBusiness === true,
        location,
      });
    }
    
    // Pattern 3: Linearized format from PDF - "12/15 PILOT TRAVEL CTR #123 SUFFOLK VA 45.00"
    const pattern3 = /(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?\s+([A-Z][A-Za-z0-9\s\*\#\-\.\/\&\'\,]+?)\s+(\d+\.?\d{0,2})\s/gi;
    
    let match3;
    while ((match3 = pattern3.exec(text)) !== null) {
      const [, month, day, description, amountStr] = match3;
      
      // Skip summary/header lines
      if (description.includes('Statement') ||
          description.includes('Account') ||
          description.includes('Balance') ||
          description.includes('Payment Due') ||
          description.includes('Credit Limit')) {
        continue;
      }
      
      const amount = parseFloat(amountStr);
      if (isNaN(amount) || amount === 0 || amount > 50000) continue; // Skip unrealistic amounts
      
      // Determine correct year - December in a year-boundary statement uses previous year
      const monthNum = parseInt(month);
      const transYear = (isYearBoundary && monthNum === 12) ? prevYear : year;
      
      const dateStr = `${transYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const exists = transactions.some(t => t.date === dateStr && Math.abs(t.amount - amount) < 0.01);
      if (exists) continue;
      
      const descTrimmed = description.trim();
      const { category, isBusiness } = categorizeTransaction(descTrimmed, amount);
      const location = extractLocation(descTrimmed);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: dateStr,
        description: descTrimmed,
        amount,
        category,
        isBusiness,
        isIncome: false,
        selected: isBusiness === true,
        location,
      });
    }
    
    console.log('Capital One CC: Found', transactions.length, 'transactions');
    return transactions;
  };

  // Parse linearized/table format (from PDF copy)
  const parseLinearizedStatement = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    // Try to find year from "STATEMENT PERIOD" or "Jan 1 - Jan 31, 2020"
    const yearMatch = text.match(/(?:STATEMENT PERIOD|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[^2]*(\d{4})/i);
    const year = yearMatch ? yearMatch[1] : currentYear.toString();
    
    // Pattern for table rows: | Date | Description | Category | Amount | Balance |
    const rowPattern = /\|\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*\|([^|]+)\|[^|]*\|\s*([+-])?\s*\$?([\d,]+\.?\d*)\s*\|\s*\$?([\d,]+\.?\d*)\s*\|/gi;
    
    let match;
    while ((match = rowPattern.exec(text)) !== null) {
      const [, month, day, description, sign, amountStr, balanceStr] = match;
      
      // Skip non-transaction rows
      if (description.includes('Opening Balance') || 
          description.includes('Closing Balance') ||
          description.includes('Interest Rate Change') ||
          !amountStr) {
        continue;
      }
      
      const amount = parseFloat(amountStr.replace(/,/g, ''));
      const balance = parseFloat(balanceStr?.replace(/,/g, '') || '0');
      const isIncome = sign === '+' || (!sign && description.toLowerCase().includes('deposit'));
      
      const monthNum = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
        .indexOf(month.toLowerCase()) + 1;
      
      const { category, isBusiness } = categorizeTransaction(description.trim(), amount);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: `${year}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
        description: description.trim(),
        amount,
        balance,
        category,
        isBusiness: isIncome ? true : isBusiness,
        isIncome,
        selected: isBusiness === true || isIncome,
      });
    }
    
    return transactions;
  };

  // Parse Navy Federal Credit Union (NFCU) statement format
  // Format: | Date | Transaction Detail | Amount($) | Balance($) |
  // Example: | 01-07 | POS Debit - Debit Card 6373 Transaction 01-06-20 Pilot #1049 Suffolk VA | 50.00- | 1,456.24 |
  // GPA Format: | 01-08 | POS Debit- Business Debit Card 8405 01-07-20 Pilot_00683 Kenly NC | 32.17- | 1,166.94 |
  const parseNFCUStatement = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    // Try to find year from statement period - multiple formats:
    // "12/27/19 - 01/26/20" or "01/01/20 - 01/31/20" or "Statement Period 01/01/20 - 01/31/20"
    let startYear = '2019'; // Default
    let endYear = '2020'; // Default
    let startMonth = 1;
    let endMonth = 12;
    
    // Try format: "12/27/19 - 01/26/20" (MM/DD/YY - MM/DD/YY)
    const yearMatch1 = text.match(/(\d{2})\/(\d{2})\/(\d{2})\s*-\s*(\d{2})\/(\d{2})\/(\d{2})/i);
    if (yearMatch1) {
      startMonth = parseInt(yearMatch1[1]);
      const startYr = parseInt(yearMatch1[3]);
      startYear = startYr > 50 ? `19${yearMatch1[3]}` : `20${yearMatch1[3]}`;
      
      endMonth = parseInt(yearMatch1[4]);
      const endYr = parseInt(yearMatch1[6]);
      endYear = endYr > 50 ? `19${yearMatch1[6]}` : `20${yearMatch1[6]}`;
      
      console.log('NFCU: Found date range:', `${startMonth}/${startYear} - ${endMonth}/${endYear}`);
    }
    
    // Also try "Statement Period" header
    const yearMatch2 = text.match(/Statement\s*Period[^0-9]*(\d{2})\/\d{2}\/(\d{2})/i);
    if (!yearMatch1 && yearMatch2) {
      const yr = parseInt(yearMatch2[2]);
      endYear = yr > 50 ? `19${yearMatch2[2]}` : `20${yearMatch2[2]}`;
      startYear = endYear; // Same year if no range found
      console.log('NFCU: Found year from Statement Period:', endYear);
    }
    
    // Determine if statement spans year boundary (e.g., Dec 2019 - Jan 2020)
    const spansYearBoundary = startYear !== endYear;
    console.log('NFCU Parser: Start year:', startYear, 'End year:', endYear, 'Spans boundary:', spansYearBoundary);
    
    // Helper to determine correct year for a transaction based on its month
    const getYearForMonth = (transMonth: number): string => {
      if (!spansYearBoundary) return endYear;
      // If spans year boundary: Dec transactions = startYear, Jan+ = endYear
      if (transMonth >= startMonth) return startYear; // e.g., month 12 >= startMonth 12
      return endYear; // e.g., month 1 < startMonth 12
    };
    
    // Pattern for NFCU transactions:
    // | MM-DD | Description | Amount- or Amount | Balance |
    // GPA format: | 01-08 | POS Debit- Business Debit Card 8405 01-07-20 Pilot_00683 Kenly NC | 32.17- | 1,166.94 |
    // Captures the trailing minus sign in group 5
    const rowPattern = /\|\s*(\d{2})-(\d{2})\s*\|([^|]+)\|\s*([\d,]+\.?\d*)(-?)\s*\|\s*([\d,]+\.?\d*)\s*\|/gi;
    
    let match;
    while ((match = rowPattern.exec(text)) !== null) {
      const [fullMatch, month, day, description, amountStr, minusSign, balanceStr] = match;
      
      // Skip non-transaction rows
      if (description.includes('Beginning Balance') || 
          description.includes('Ending Balance') ||
          description.includes('No Transactions') ||
          description.includes('Transaction Detail') ||
          description.includes('Amount($)')) {
        continue;
      }
      
      // Parse amount - NFCU uses "50.00-" for debits (minus at end)
      const rawAmount = amountStr.replace(/,/g, '');
      const isDebit = minusSign === '-';
      const amount = parseFloat(rawAmount);
      const balance = parseFloat(balanceStr?.replace(/,/g, '') || '0');
      
      if (isNaN(amount) || amount === 0) continue;
      
      const transMonth = parseInt(month);
      console.log('NFCU row:', { month, day, description: description.substring(0, 50), amount, isDebit });
      
      // Determine if income based on description
      const descLower = description.toLowerCase();
      const isIncome = descLower.includes('deposit') || 
                       descLower.includes('transfer from') ||
                       descLower.includes('ach paid to') === false && !isDebit;
      
      // Try to extract actual transaction date from description (Transaction MM-DD-YY)
      // Otherwise use the correct year based on month and statement period
      let transYear = getYearForMonth(transMonth);
      const transDateMatch = description.match(/Transaction\s+(\d{2})-(\d{2})-(\d{2})/i);
      if (transDateMatch) {
        const yr = parseInt(transDateMatch[3]);
        transYear = yr > 50 ? `19${transDateMatch[3]}` : `20${transDateMatch[3]}`;
      }
      
      const descTrimmed = description.trim();
      const { category, isBusiness } = categorizeTransaction(descTrimmed, amount);
      const location = extractLocation(descTrimmed);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: `${transYear}-${month}-${day.padStart(2, '0')}`,
        description: descTrimmed,
        amount,
        balance,
        category,
        isBusiness: isIncome && !descLower.includes('transfer to') ? true : isBusiness,
        isIncome: isIncome && !isDebit,
        selected: (isBusiness === true || isIncome) && !descLower.includes('transfer'),
        location,
      });
    }
    
    // Also try line-by-line format (non-table) - OCR format from Adobe:
    // 01-07 POS Debit - Debit Card 6373 Transaction 01-06-20 Pilot #1049 Suffolk VA 50.00- 1,456.24
    // Format: MM-DD Description Amount(-) Balance
    if (transactions.length < 3) {
      console.log('Trying NFCU OCR line pattern');
      
      // Pattern: date at start, amounts at end (amount may have trailing minus)
      // Matches: "01-07 POS Debit - ... Suffolk VA 50.00- 1,456.24"
      const linePattern = /(\d{2})-(\d{2})\s+(.+?)\s+([\d,]+\.\d{2})(-?)\s+([\d,]+\.\d{2})/g;
      
      let lineMatch;
      while ((lineMatch = linePattern.exec(text)) !== null) {
        const [, month, day, description, amountStr, minusSign, balanceStr] = lineMatch;
        
        // Skip non-transaction lines
        if (description.includes('Beginning Balance') || 
            description.includes('Ending Balance') ||
            description.includes('Transaction Detail') ||
            description.includes('Average Daily Balance')) {
          continue;
        }
        
        const amount = parseFloat(amountStr.replace(/,/g, ''));
        const balance = parseFloat(balanceStr?.replace(/,/g, '') || '0');
        const isDebit = minusSign === '-';
        const descLower = description.toLowerCase();
        const isIncome = !isDebit && (descLower.includes('deposit') || descLower.includes('transfer from'));
        const transMonth = parseInt(month);
        
        if (isNaN(amount) || amount === 0) continue;
        
        // Use correct year based on month and statement period
        let transYear = getYearForMonth(transMonth);
        const transDateMatch = description.match(/(\d{2})-(\d{2})-(\d{2})/);
        if (transDateMatch) {
          const yr = parseInt(transDateMatch[3]);
          transYear = yr > 50 ? `19${transDateMatch[3]}` : `20${transDateMatch[3]}`;
        }
        
        const descTrimmed = description.trim();
        const { category, isBusiness } = categorizeTransaction(descTrimmed, amount);
        const location = extractLocation(descTrimmed);
        
        // Avoid duplicates
        const dateStr = `${transYear}-${month}-${day.padStart(2, '0')}`;
        const exists = transactions.some(t => 
          t.date === dateStr && 
          Math.abs(t.amount - amount) < 0.01
        );
        
        if (!exists) {
          console.log('NFCU OCR line:', { date: dateStr, desc: description.substring(0, 40), amount, isDebit });
          transactions.push({
            id: crypto.randomUUID(),
            date: dateStr,
            description: descTrimmed,
            amount,
            balance,
            category,
            isBusiness: isDebit ? isBusiness : false, // Only debits can be business expenses
            isIncome,
            selected: isDebit && isBusiness === true,
            location,
          });
        }
      }
      
      console.log('NFCU OCR pattern found:', transactions.length, 'transactions');
    }
    
    // Pattern 3: Simple format without balance - just "MM-DD Description Amount-"
    if (transactions.length < 3) {
      console.log('Trying simple NFCU pattern');
      const simplePattern = /(\d{2})-(\d{2})\s+((?:POS Debit|Deposit|Transfer|ACH|Paid)[^0-9]+?)\s+([\d,]+\.\d{2})(-?)/g;
      
      let simpleMatch;
      while ((simpleMatch = simplePattern.exec(text)) !== null) {
        const [, month, day, description, amountStr, minusSign] = simpleMatch;
        
        const amount = parseFloat(amountStr.replace(/,/g, ''));
        const isDebit = minusSign === '-';
        const transMonth = parseInt(month);
        
        if (isNaN(amount) || amount === 0) continue;
        
        const descLower = description.toLowerCase();
        const isIncome = !isDebit && (descLower.includes('deposit') || descLower.includes('transfer from'));
        
        // Use correct year based on month and statement period
        let transYear = getYearForMonth(transMonth);
        const transDateMatch = description.match(/(\d{2})-(\d{2})-(\d{2})/);
        if (transDateMatch) {
          const yr = parseInt(transDateMatch[3]);
          transYear = yr > 50 ? `19${transDateMatch[3]}` : `20${transDateMatch[3]}`;
        }
        
        const { category, isBusiness } = categorizeTransaction(description.trim(), amount);
        
        const dateStr = `${transYear}-${month}-${day.padStart(2, '0')}`;
        const exists = transactions.some(t => 
          t.date === dateStr && 
          Math.abs(t.amount - amount) < 0.01
        );
        
        if (!exists) {
          transactions.push({
            id: crypto.randomUUID(),
            date: dateStr,
            description: description.trim(),
            amount,
            category,
            isBusiness: isDebit ? isBusiness : false,
            isIncome,
            selected: isDebit && isBusiness === true,
          });
        }
      }
      
      console.log('NFCU simple pattern found:', transactions.length, 'transactions');
    }
    
    return transactions;
  };

  // Extract text from PDF file
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      // Group items by Y position to reconstruct rows
      const items = textContent.items as any[];
      const rows: Map<number, string[]> = new Map();
      
      for (const item of items) {
        if (!item.str.trim()) continue;
        // Round Y to group items on same line (PDFs have slight variations)
        const y = Math.round(item.transform[5]);
        if (!rows.has(y)) {
          rows.set(y, []);
        }
        rows.get(y)!.push(item.str);
      }
      
      // Sort rows by Y position (descending since PDF Y is bottom-up)
      const sortedYs = Array.from(rows.keys()).sort((a, b) => b - a);
      
      for (const y of sortedYs) {
        const rowText = rows.get(y)!.join(' ');
        fullText += rowText + '\n';
      }
      
      fullText += '\n--- Page ' + i + ' ---\n\n';
    }
    
    console.log('Extracted PDF text (first 2000 chars):', fullText.substring(0, 2000));
    return fullText;
  };

  // Parse SmartFunds Card Activity Report (fuel card from Quality Driveaway, etc.)
  const parseSmartFundsReport = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    console.log('SmartFunds: Starting parse...');
    console.log('SmartFunds: Text sample:', text.substring(0, 500));
    
    // Extract date range
    const dateRangeMatch = text.match(/(\d{4}-\d{2}-\d{2})\s*From:|From:\s*(\d{4}-\d{2}-\d{2})/i);
    const year = dateRangeMatch ? (dateRangeMatch[1] || dateRangeMatch[2]).substring(0, 4) : currentYear.toString();
    console.log('SmartFunds report year:', year);
    
    // Try to parse table format first (when pasted from PDF with structure preserved)
    // Format: | WDRW | 2025-12-18 15:37 | BP#9415589LINCOLNWA GOSHEN | IN | 66223037 | | | FUEL | 089631 | 38.03 | 0.47 |
    // Columns: Type | Date Time | Merchant | City | State/Prov | Reference ID | User | Products | Invoice | Amount | Balance
    const tableRowPattern = /\|\s*(WDRW|LOAD|BBAL|DLRF|DTCU|DACT)\s*\|\s*(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})\s*\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|([^|]*)\|/gi;
    
    let tableMatch;
    while ((tableMatch = tableRowPattern.exec(text)) !== null) {
      const [, type, dateStr, time, merchantCol, cityCol, stateCol, refIdCol, userCol, productsCol, invoiceCol, amountCol, balanceCol] = tableMatch;
      
      // Skip LOAD (settlements), fees, and beginning balance
      if (type === 'LOAD' || type === 'BBAL' || type === 'DLRF' || type === 'DTCU' || type === 'DACT') continue;
      
      const merchant = merchantCol.trim();
      const city = cityCol.trim();
      const state = stateCol.trim();
      const products = productsCol.trim();
      const amount = parseFloat(amountCol.replace(/[^0-9.]/g, ''));
      
      if (isNaN(amount) || amount < 1) continue;
      
      // Skip cash advances
      if (products.includes('CADV') || merchant.includes('ACH_WIRE')) continue;
      
      const location = (city && state) ? `${city}, ${state}` : undefined;
      
      // Determine category
      let category = 'miscellaneous';
      if (products.includes('ULSD') || products.includes('FUEL') || products.includes('DSL')) {
        category = 'fuel';
      } else if (products.includes('MERC')) {
        category = 'otr_misc';
      } else if (/LOVE|PILOT|FJ|TA |7-11|FLYING|BP/i.test(merchant)) {
        category = amount > 30 ? 'fuel' : 'otr_misc';
      }
      
      console.log(`SmartFunds table: ${type} | ${dateStr} | "${merchant}" | ${city}, ${state} | ${products} | $${amount}`);
      
      transactions.push({
        id: crypto.randomUUID(),
        date: dateStr,
        description: merchant,
        amount,
        category,
        isBusiness: true,
        isIncome: false,
        selected: true,
        bankSource: 'Other',
        location
      });
    }
    
    // If table parsing found transactions, return them
    if (transactions.length > 0) {
      console.log('SmartFunds: Parsed from table format:', transactions.length, 'transactions');
      return transactions;
    }
    
    // Otherwise fall back to linearized format parsing
    console.log('SmartFunds: No table format detected, trying linearized format');
    
    // SmartFunds format after PDF extraction/paste is linearized. Examples:
    // WDRW 2025-04-21 08:48 FJ-DAVENPORT 636 ULSD 0597982451 45.96 250.98
    // LOAD 2025-04-18 17:39 Leg#:780416 tmw184707 294.42 296.94
    // WDRW 2025-04-21 14:05 LOVE S #0476 INSIDE DAVENPORT IA 60443410 MERC,MERC 593161 78.65 172.33
    
    // CRITICAL: Remove summary section at bottom of report first!
    // Summary looks like: Beginning Balance -$ 0.11 DACT 0.50 LOAD 377.22 WDRW 375.04 DLRF 1.00 Ending Balance $ 0.57
    // These totals get mixed in and cause wrong amounts to be picked up
    let cleanedText = text;
    const summaryMatch = text.match(/Beginning\s*Balance[\s\S]*?Ending\s*Balance[^]*?(?:\d+\.\d{2}|\$\s*[\d.]+)/i);
    if (summaryMatch) {
      cleanedText = text.replace(summaryMatch[0], '');
      console.log('SmartFunds: Removed summary section');
    }
    // Also remove "Page X of Y" footers
    cleanedText = cleanedText.replace(/Page\s+\d+\s+of\s+\d+/gi, '');
    
    // Strategy: Find each TYPE DATE TIME, then extract merchant and amounts
    // Split by transaction types to get each transaction block
    const transBlocks = cleanedText.split(/(?=(?:WDRW|LOAD|BBAL|DLRF|DTCU|DACT)\s+\d{4}-\d{2}-\d{2})/i);
    
    for (const block of transBlocks) {
      if (!block.trim()) continue;
      
      // Extract type and date
      const headerMatch = block.match(/^(WDRW|LOAD|BBAL|DLRF|DTCU|DACT)\s+(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/i);
      if (!headerMatch) continue;
      
      const type = headerMatch[1].toUpperCase();
      const dateStr = headerMatch[2];
      
      // Skip LOAD (settlements - use portal instead to avoid duplicates)
      // Skip fees and beginning balance
      if (type === 'LOAD' || type === 'BBAL' || type === 'DLRF' || type === 'DTCU' || type === 'DACT') continue;
      
      // Get the rest of the line after datetime
      const rest = block.substring(headerMatch[0].length).trim();
      
      // Find all decimal numbers (XX.XX format) - these are amounts and balances
      const decimalMatches = rest.match(/\d+\.\d{2}/g) || [];
      if (decimalMatches.length < 2) continue; // Need at least amount and balance
      
      // Convert to numbers
      const decimalValues = decimalMatches.map(m => parseFloat(m));
      
      console.log(`SmartFunds: Found decimals in block: ${decimalMatches.join(', ')}`);
      
      // The table format is: ... | Invoice Number | Amount | Balance |
      // PDF extraction often reverses the order, so we need to be smart about which is which
      // 
      // Key insight: The AMOUNT is what was spent, typically a reasonable transaction amount
      // The BALANCE is the running total, which can be any value
      //
      // For SmartFunds WDRW transactions:
      // - Fuel amounts: usually $30-$200
      // - Tolls/misc: usually $1-$50
      // - Balance can be anything from $0.01 to $500+
      //
      // Strategy: Look at the last two decimals. The smaller one that's > $1 is likely the amount
      // UNLESS both are similar, in which case use position
      const lastTwo = decimalValues.slice(-2);
      let amount: number;
      let balance: number;
      
      // Sort to find smaller and larger
      const [smaller, larger] = [...lastTwo].sort((a, b) => a - b);
      
      // If there's a clear difference (one is much bigger), smaller is likely the transaction amount
      // Exception: if smaller is very tiny (< $1), it's probably the balance (nearly depleted card)
      if (smaller < 1 && larger > 1) {
        // Tiny balance, larger is the amount
        amount = larger;
        balance = smaller;
      } else if (larger > smaller * 2.5) {
        // Big difference - smaller is likely the transaction amount
        amount = smaller;
        balance = larger;
      } else {
        // Similar values - PDF extraction typically has [balance, amount] order
        // So the SECOND value (lastTwo[1]) is the amount
        amount = lastTwo[1];
        balance = lastTwo[0];
      }
      
      // Skip if amount is unreasonable
      if (amount < 0.50 || amount > 10000) continue;
      
      console.log(`SmartFunds raw: decimals=${decimalMatches.join(',')} | picked amount=${amount} | balance=${balance}`);
      
      // Extract merchant name (everything before the amounts, excluding codes)
      let merchantPart = rest;
      // Remove the amount/balance from the end
      for (let i = decimalMatches.length - 1; i >= 0; i--) {
        merchantPart = merchantPart.replace(decimalMatches[i], '').trim();
      }
      
      // Cut off at report structure keywords (stop at first occurrence)
      const cutoffMatch = merchantPart.match(/\s+(Beginning Balance|Ending Balance|BBAL|DLRF|DTCU|DACT|Page \d+)/i);
      if (cutoffMatch) {
        merchantPart = merchantPart.substring(0, cutoffMatch.index).trim();
      }
      
      // Detect product type
      let products = '';
      if (/ULSD|DSL/i.test(merchantPart)) products = 'ULSD';
      else if (/\bFUEL\b/i.test(merchantPart)) products = 'FUEL';
      else if (/MERC/i.test(merchantPart)) products = 'MERC';
      else if (/CADV/i.test(merchantPart)) products = 'CADV';
      
      // Clean up merchant name - remove product codes, invoice numbers, report structure
      let merchant = merchantPart
        // Remove report structure keywords
        .replace(/\$?\s*(Beginning Balance|Ending Balance|LOAD|WDRW|BBAL|DLRF|DTCU|DACT|Page \d+.*$)/gi, '')
        // Remove product codes
        .replace(/\b(ULSD|FUEL|MERC|CADV|DSL)\b/gi, '')
        // Remove state + ref number patterns
        .replace(/\b[A-Z]{2}\s+\d{6,}/g, '')
        // Remove long invoice numbers
        .replace(/\b\d{6,}\b/g, '')
        // Remove extra whitespace
        .replace(/\s+/g, ' ')
        .trim();
      
      // Handle LOAD (settlements)
      if (type === 'LOAD') {
        const legMatch = rest.match(/Leg#:(\d+)/i);
        const isSettlement = /SETTLEMENT|Batch/i.test(rest);
        
        if (legMatch) {
          merchant = `Leg#:${legMatch[1]}`;
        } else if (isSettlement) {
          merchant = 'Settlement';
        }
      }
      
      console.log(`SmartFunds: ${type} | ${dateStr} | "${merchant}" | ${products} | $${amount}`);
      
      // Skip tiny amounts
      if (amount < 1) continue;
      
      // Determine category
      let category = 'miscellaneous';
      let isBusiness: boolean | null = true;
      let isIncome = false;
      let description = merchant;
      
      if (type === 'LOAD') {
        isIncome = true;
        category = 'income';
        description = `💰 ${merchant}`;
      } else if (type === 'WDRW') {
        // Skip cash advances
        if (products === 'CADV' || /ACH|WIRE/i.test(merchant)) {
          console.log('SmartFunds: Skipping cash advance:', merchant);
          continue;
        }
        
        if (products === 'ULSD' || products === 'FUEL') {
          category = 'fuel';
          description = `⛽ ${merchant}`;
        } else if (products === 'MERC') {
          category = 'otr_misc';
          description = `🛒 ${merchant}`;
        } else if (/LOVE|PILOT|FJ-|TA |7-11|FLYING/i.test(merchant)) {
          // Truck stop - categorize by amount
          if (amount > 30) {
            category = 'fuel';
            description = `⛽ ${merchant}`;
          } else {
            category = 'otr_misc';
            description = `🛒 ${merchant}`;
          }
        } else {
          isBusiness = null;
        }
      }
      
      // Check duplicates
      const exists = transactions.some(t => 
        t.date === dateStr && Math.abs(t.amount - amount) < 0.02
      );
      
      if (!exists && merchant.length > 0) {
        const location = extractLocation(merchant);
        transactions.push({
          id: crypto.randomUUID(),
          date: dateStr,
          description: description || merchant,
          amount,
          category,
          isBusiness,
          isIncome,
          selected: isBusiness === true,
          bankSource: 'Other',
          location
        });
      }
    }
    
    console.log('SmartFunds parsed transactions:', transactions.length);
    return transactions;
  };

  // Parse Love's Travel Stops Invoice (GPA fuel card)
  // Format: Date | Truck ID | City | State | Ticket | PO# | Fuel Tot | ... | Net
  const parseLovesInvoice = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    console.log('Loves Invoice: Starting parse...');
    
    // Pattern for Love's invoice table rows
    // Format: | 02/19/2020 | 1726 | Dandridge | TN | 1 | 2173826277 | $60.79 | $0.00 | $0.00 | $0.00 | $0.00 | $0.00 | 60.79 | 2.67 | 58.12 | ...
    const tablePattern = /\|\s*(\d{2}\/\d{2}\/\d{4})\s*\|\s*(\d+)\s*\|\s*([^|]+?)\s*\|\s*([A-Z]{2})\s*\|\s*(\d+)\s*\|\s*(\d+)\s*\|\s*\$?([\d.]+)\s*\|/gi;
    
    let match;
    while ((match = tablePattern.exec(text)) !== null) {
      const [, dateStr, truckId, city, state, ticket, po, fuelTotStr] = match;
      
      const fuelTot = parseFloat(fuelTotStr);
      if (isNaN(fuelTot) || fuelTot < 1) continue;
      
      // Parse date from MM/DD/YYYY to YYYY-MM-DD
      const [month, day, year] = dateStr.split('/');
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      const location = `${city.trim()}, ${state}`;
      
      // Check for duplicates
      const exists = transactions.some(t => 
        t.date === formattedDate && Math.abs(t.amount - fuelTot) < 0.02
      );
      
      if (!exists) {
        transactions.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          description: `⛽ Love's #${ticket} - ${location}`,
          amount: fuelTot,
          category: 'fuel',
          isBusiness: true,
          isIncome: false,
          selected: true,
          bankSource: 'Other',
          location
        });
        console.log(`Loves: ${formattedDate} | ${location} | Ticket #${ticket} | $${fuelTot}`);
      }
    }
    
    // Also try simpler pattern for text extraction variations
    // Date Truck City State Ticket PO FuelTot ...
    if (transactions.length === 0) {
      const simplePattern = /(\d{2}\/\d{2}\/\d{4})\s+(\d+)\s+([A-Za-z\s]+?)\s+([A-Z]{2})\s+(\d+)\s+(\d{10})\s+\$?([\d.]+)/gi;
      
      while ((match = simplePattern.exec(text)) !== null) {
        const [, dateStr, truckId, city, state, ticket, po, fuelTotStr] = match;
        
        const fuelTot = parseFloat(fuelTotStr);
        if (isNaN(fuelTot) || fuelTot < 1) continue;
        
        const [month, day, year] = dateStr.split('/');
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        
        const location = `${city.trim()}, ${state}`;
        
        const exists = transactions.some(t => 
          t.date === formattedDate && Math.abs(t.amount - fuelTot) < 0.02
        );
        
        if (!exists) {
          transactions.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: `⛽ Love's #${ticket} - ${location}`,
            amount: fuelTot,
            category: 'fuel',
            isBusiness: true,
            isIncome: false,
            selected: true,
            bankSource: 'Other',
            location
          });
        }
      }
    }
    
    console.log('Loves Invoice parsed:', transactions.length, 'transactions');
    return transactions;
  };

  // Parse Love's Individual Receipts (detailed fuel receipts with location and odometer)
  // Uses pattern-based extraction to find fuel transactions in messy PDF text
  const parseLovesReceipts = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    console.log('=== LOVES RECEIPT PARSER ===');
    console.log('Input text length:', text.length);
    console.log('RAW First 500 chars:', text.substring(0, 500));
    
    // STEP 1: Aggressive pre-processing to fix PDF fragmentation
    // The PDF extractor often splits words with spaces
    let normalized = text
      // Fix common word splits
      .replace(/T\s*R\s*K\s*D\s*S/gi, 'TRKDS')      // "T R K D S" -> "TRKDS"
      .replace(/TRKD\s+S/gi, 'TRKDS')               // "TRKD S" -> "TRKDS"
      .replace(/S\s*T\s*O\s*R\s*E/gi, 'STORE')      // "S T O R E" -> "STORE"
      .replace(/D\s*F\s*C\s*Q/gi, 'DFCQ')           // "D F C Q" -> "DFCQ"
      .replace(/Ga\s*l\s*lons/gi, 'Gallons')        // "Ga l lons" -> "Gallons"
      .replace(/Odo\s*m\s*et\s*er/gi, 'Odometer')   // "Odo m et er" -> "Odometer"
      .replace(/INVOICE\s*#?\s*/gi, 'INVOICE#')     // "INVOICE # " -> "INVOICE#"
      .replace(/Hu\s*l\s*l/gi, 'Hull')              // "Hu l l" -> "Hull" (Hope Hull, AL)
      .replace(/(\d)\s*\.\s*(\d)/g, '$1.$2')        // "31 . 64" -> "31.64"
      .replace(/(\d)\s+(\d{2})(?=\s|$)/g, '$1.$2')  // "57 61" -> "57.61" (amounts)
      .replace(/\$\s+/g, '$')                       // "$ 31" -> "$31"
      .replace(/,\s*([A-Z])\s+([A-Z])(?=\s|\()/g, ', $1$2')  // ", A L" -> ", AL"
      .replace(/([A-Z])\s+([A-Z])(?=\s*\(|\s*$)/g, '$1$2');  // State codes
    
    console.log('NORMALIZED First 500 chars:', normalized.substring(0, 500));
    
    // APPROACH: Find all TRKDS entries with their amounts, then extract nearby context
    // TRKDS = Truck Diesel fuel code at Love's
    
    // Pattern to find fuel entries: look for TRKDS followed by amount
    // In table format: "| TRKDS | | 31.64 |" or linearized "TRKDS 31.64"
    const fuelPattern = /TRKDS[\s|]*([\d]+\.[\d]{2})/gi;
    const defPattern = /\bDEF[\s|]*([\d]+\.[\d]{2})/gi;
    
    // Also try to find amounts near DFCQ (fleet card payment indicator)
    // Format: "DFCQ 31.64" or "DFCQ | 31.64"
    const dfcqPattern = /DFCQ[\s|]*\*{0,6}\d{0,4}[\s|]*([\d]+\.[\d]{2})/gi;
    
    // Find all dates in the text
    const datePattern = /(\d{2})\/(\d{2})\/(\d{4})/g;
    const dates = [...normalized.matchAll(datePattern)];
    console.log('Dates found:', dates.length);
    
    // Find all TRKDS amounts
    const trkdsMatches = [...normalized.matchAll(fuelPattern)];
    console.log('TRKDS matches:', trkdsMatches.length, trkdsMatches.map(m => m[1]));
    
    // Find all DEF amounts  
    const defMatches = [...normalized.matchAll(defPattern)];
    console.log('DEF matches:', defMatches.length, defMatches.map(m => m[1]));
    
    // Find all DFCQ payments
    const dfcqMatches = [...normalized.matchAll(dfcqPattern)];
    console.log('DFCQ matches:', dfcqMatches.length, dfcqMatches.map(m => m[1]));
    
    // Find store numbers - already normalized to STORE
    const storePattern = /STORE\s*(\d{3,4})/gi;
    const storeMatches = [...normalized.matchAll(storePattern)];
    console.log('Store matches:', storeMatches.length);
    
    // Find gallons - already normalized to Gallons
    const gallonsPattern = /Gallons:?\s*[\s|]*([\d]+\.[\d]+)/gi;
    const gallonsMatches = [...normalized.matchAll(gallonsPattern)];
    console.log('Gallons matches:', gallonsMatches.length, gallonsMatches.map(m => m[1]));
    
    // Find odometer readings - already normalized to Odometer
    const odoPattern = /Odometer:?\s*[\s|]*([\d,]+)/gi;
    const odoMatches = [...normalized.matchAll(odoPattern)];
    console.log('Odometer matches:', odoMatches.length);
    
    // Find locations - "City, ST" pattern
    const locationPattern = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*([A-Z]{2})(?:\s|\(|$)/g;
    const locationMatches = [...normalized.matchAll(locationPattern)];
    console.log('Location matches:', locationMatches.length, locationMatches.map(m => `${m[1]}, ${m[2]}`));
    
    // Find invoice numbers - already normalized to INVOICE#
    const invoicePattern = /INVOICE#(\d{5,6})/gi;
    const invoiceMatches = [...normalized.matchAll(invoicePattern)];
    console.log('Invoice matches:', invoiceMatches.length);
    
    // Now correlate: for each TRKDS amount, find the nearest date and build a transaction
    // Use DFCQ matches if no TRKDS found (DFCQ = fleet card payment for fuel)
    
    const fuelAmounts = trkdsMatches.length > 0 ? trkdsMatches : dfcqMatches;
    
    if (fuelAmounts.length === 0) {
      console.log('No TRKDS or DFCQ fuel amounts found');
      
      // Last resort: look for "Total Sale: $XX.XX" with nearby fuel indicators
      const totalSalePattern = /Total\s*Sale:?\s*\$?([\d]+\.[\d]{2})/gi;
      const totals = [...normalized.matchAll(totalSalePattern)];
      console.log('Total Sale matches:', totals.length, totals.map(m => m[1]));
      
      // Filter to likely fuel amounts (> $25)
      for (let i = 0; i < totals.length; i++) {
        const amount = parseFloat(totals[i][1]);
        if (amount < 25 || amount > 200) continue; // Skip non-fuel amounts
        
        // Find nearby date
        const totalPos = totals[i].index || 0;
        let nearestDate = dates[0];
        let minDist = Infinity;
        
        for (const dateMatch of dates) {
          const datePos = dateMatch.index || 0;
          const dist = Math.abs(datePos - totalPos);
          if (dist < minDist) {
            minDist = dist;
            nearestDate = dateMatch;
          }
        }
        
        if (!nearestDate) continue;
        
        const [, month, day, year] = nearestDate;
        const formattedDate = `${year}-${month}-${day}`;
        
        // Check for duplicates
        const exists = transactions.some(t => 
          t.date === formattedDate && Math.abs(t.amount - amount) < 2.00
        );
        
        if (!exists) {
          transactions.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: `⛽ Love's Fuel - $${amount.toFixed(2)}`,
            amount,
            category: 'fuel',
            isBusiness: true,
            isIncome: false,
            selected: true,
            bankSource: 'GPA'
          });
          console.log(`✅ Added from Total Sale: ${formattedDate} | $${amount}`);
        }
      }
    } else {
      // Process each TRKDS/DFCQ fuel entry
      for (let i = 0; i < fuelAmounts.length; i++) {
        const fuelMatch = fuelAmounts[i];
        const amount = parseFloat(fuelMatch[1]);
        const fuelPos = fuelMatch.index || 0;
        
        if (amount < 20 || amount > 200) continue; // Skip non-fuel amounts
        
        // Find nearest date (before this position preferably)
        let nearestDate = dates[0];
        let minDist = Infinity;
        
        for (const dateMatch of dates) {
          const datePos = dateMatch.index || 0;
          // Prefer dates that come before the amount
          const dist = Math.abs(datePos - fuelPos);
          if (dist < minDist) {
            minDist = dist;
            nearestDate = dateMatch;
          }
        }
        
        if (!nearestDate) continue;
        
        const [, month, day, year] = nearestDate;
        const formattedDate = `${year}-${month}-${day}`;
        
        // Find nearest store
        let storeNum = '';
        minDist = Infinity;
        for (const storeMatch of storeMatches) {
          const storePos = storeMatch.index || 0;
          const dist = Math.abs(storePos - fuelPos);
          if (dist < minDist) {
            minDist = dist;
            storeNum = storeMatch[1] || storeMatch[2] || '';
          }
        }
        
        // Find nearest location
        let location = '';
        minDist = Infinity;
        for (const locMatch of locationMatches) {
          const locPos = locMatch.index || 0;
          const dist = Math.abs(locPos - fuelPos);
          if (dist < minDist && dist < 500) { // Must be within 500 chars
            minDist = dist;
            location = `${locMatch[1]}, ${locMatch[2]}`;
          }
        }
        
        // Find nearest gallons
        let gallons = 0;
        minDist = Infinity;
        for (const galMatch of gallonsMatches) {
          const galPos = galMatch.index || 0;
          const dist = Math.abs(galPos - fuelPos);
          if (dist < minDist && dist < 300) {
            minDist = dist;
            gallons = parseFloat(galMatch[1]);
          }
        }
        
        // Find nearest odometer
        let odometer = '';
        minDist = Infinity;
        for (const odoMatch of odoMatches) {
          const odoPos = odoMatch.index || 0;
          const dist = Math.abs(odoPos - fuelPos);
          if (dist < minDist && dist < 300) {
            minDist = dist;
            odometer = odoMatch[1].replace(/,/g, '');
          }
        }
        
        // Find nearest invoice
        let invoice = '';
        minDist = Infinity;
        for (const invMatch of invoiceMatches) {
          const invPos = invMatch.index || 0;
          const dist = Math.abs(invPos - fuelPos);
          if (dist < minDist && dist < 300) {
            minDist = dist;
            invoice = invMatch[1];
          }
        }
        
        // Check for duplicates (same date + similar amount)
        const exists = transactions.some(t => 
          t.date === formattedDate && Math.abs(t.amount - amount) < 2.00
        );
        
        if (!exists) {
          let desc = `⛽ Love's`;
          if (storeNum) desc += ` #${storeNum}`;
          if (location) desc += ` - ${location}`;
          if (gallons > 0) desc += ` (${gallons.toFixed(1)} gal)`;
          if (odometer) desc += ` [ODO: ${odometer}]`;
          if (invoice) desc += ` INV#${invoice}`;
          
          transactions.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: desc,
            amount,
            category: 'fuel',
            isBusiness: true,
            isIncome: false,
            selected: true,
            bankSource: 'GPA',
            location
          });
          console.log(`✅ Love's Fuel: ${formattedDate} | ${location} | #${storeNum} | $${amount} | ${gallons} gal`);
        } else {
          console.log(`⏭️ Duplicate: ${formattedDate} | $${amount}`);
        }
      }
    }
    
    console.log('=== PARSER COMPLETE: ', transactions.length, 'fuel transactions ===');
    return transactions;
  };

  // Parse TSD Logistics Agent Settlement Summary (RV Fuel deductions)
  // Format varies - can be table with pipes or linearized text
  const parseTSDFuelCard = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    console.log('TSD Fuel: Starting parse...');
    console.log('TSD text length:', text.length);
    
    // Look for all "RV Fuel" entries with their amounts
    // Format can be: "-$34.48 RV Fuel 782501848 1.00 34.480 05/01/20"
    // Or table: "RV Fuel | memo | date | 1.00 | 34.480 | -$34.48"
    
    // Pattern 1: Look for "-$XX.XX RV Fuel" pattern (amount before RV Fuel)
    const amountBeforePattern = /-?\$?([\d]+\.[\d]{2})\s+RV Fuel\s+(\d{9,})\s+[\d.]+\s+([\d.]+)\s+(\d{2}\/\d{2}\/\d{2,4})/gi;
    
    let match;
    while ((match = amountBeforePattern.exec(text)) !== null) {
      const [, amountStr, memo, rate, dateStr] = match;
      
      const amount = parseFloat(amountStr);
      if (isNaN(amount) || amount < 5) continue;
      
      const dateParts = dateStr.split('/');
      const fullYear = dateParts[2].length === 2 ? `20${dateParts[2]}` : dateParts[2];
      const formattedDate = `${fullYear}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
      
      const exists = transactions.some(t => 
        t.date === formattedDate && Math.abs(t.amount - amount) < 0.02
      );
      
      if (!exists) {
        transactions.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          description: `⛽ TSD RV Fuel #${memo}`,
          amount,
          category: 'fuel',
          isBusiness: true,
          isIncome: false,
          selected: true,
          bankSource: 'Other'
        });
        console.log(`TSD RV Fuel (pattern 1): ${formattedDate} | Memo #${memo} | $${amount}`);
      }
    }
    
    // Pattern 2: Find RV Fuel with memo number and date nearby (segment-based)
    if (transactions.length === 0) {
      const segments = text.split(/(?=-?\$[\d.]+\s+RV Fuel|RV Fuel)/gi);
      
      for (const segment of segments) {
        if (!/rv fuel/i.test(segment)) continue;
        
        // Extract memo number (9+ digits)
        const memoMatch = segment.match(/(\d{9,})/);
        const memo = memoMatch ? memoMatch[1] : 'unknown';
        
        // Extract date (MM/DD/YY or MM/DD/YYYY)
        const dateMatch = segment.match(/(\d{2})\/(\d{2})\/(\d{2,4})/);
        if (!dateMatch) continue;
        
        const [, month, day, year] = dateMatch;
        const fullYear = year.length === 2 ? `20${year}` : year;
        const formattedDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        
        // Look for amount with $ sign first
        const dollarMatch = segment.match(/-?\$(\d+\.\d{2})/);
        let amount = 0;
        
        if (dollarMatch) {
          amount = parseFloat(dollarMatch[1]);
        } else {
          // Fallback: find decimal numbers
          const amountMatches = segment.match(/(\d+\.\d{2,3})/g);
          if (amountMatches) {
            for (const amtStr of amountMatches) {
              const amt = parseFloat(amtStr);
              if (amt > 10 && amt < 200 && amt > amount) {
                amount = amt;
              }
            }
          }
        }
        
        if (amount < 5) continue;
        
        // Check for duplicates
        const exists = transactions.some(t => 
          t.date === formattedDate && Math.abs(t.amount - amount) < 0.02
        );
        
        if (!exists) {
          transactions.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: `⛽ TSD RV Fuel #${memo}`,
            amount,
            category: 'fuel',
            isBusiness: true,
            isIncome: false,
            selected: true,
            bankSource: 'Other'
          });
          console.log(`TSD RV Fuel: ${formattedDate} | Memo #${memo} | $${amount}`);
        }
      }
    }
    
    // Pattern 2: If segment parsing didn't work, try line-by-line
    if (transactions.length === 0) {
      console.log('TSD: Trying line-by-line pattern...');
      const lines = text.split('\n');
      
      for (const line of lines) {
        if (!/rv fuel/i.test(line)) continue;
        
        const memoMatch = line.match(/(\d{9,})/);
        const dateMatch = line.match(/(\d{2})\/(\d{2})\/(\d{2,4})/);
        const amountMatches = line.match(/(\d+\.\d{2,3})/g);
        
        if (!dateMatch || !amountMatches) continue;
        
        const [, month, day, year] = dateMatch;
        const fullYear = year.length === 2 ? `20${year}` : year;
        const formattedDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        
        let amount = 0;
        for (const amtStr of amountMatches) {
          const amt = parseFloat(amtStr);
          if (amt > 10 && amt < 200 && amt > amount) {
            amount = amt;
          }
        }
        
        if (amount < 5) continue;
        
        const exists = transactions.some(t => 
          t.date === formattedDate && Math.abs(t.amount - amount) < 0.02
        );
        
        if (!exists) {
          transactions.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: `⛽ TSD RV Fuel ${memoMatch ? '#' + memoMatch[1] : ''}`.trim(),
            amount,
            category: 'fuel',
            isBusiness: true,
            isIncome: false,
            selected: true,
            bankSource: 'Other'
          });
        }
      }
    }
    
    // Pattern 3: Ultra-flexible - just find dates near "RV Fuel" text and amounts
    if (transactions.length === 0) {
      console.log('TSD: Trying ultra-flexible pattern...');
      
      // Find all dates in the document
      const datePattern = /(\d{2})\/(\d{2})\/(\d{2,4})/g;
      let dateMatch;
      const seenDates = new Set<string>();
      
      while ((dateMatch = datePattern.exec(text)) !== null) {
        const [fullMatch, month, day, year] = dateMatch;
        const fullYear = year.length === 2 ? `20${year}` : year;
        const formattedDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        
        // Get context around this date (500 chars before and after)
        const start = Math.max(0, dateMatch.index - 500);
        const end = Math.min(text.length, dateMatch.index + 500);
        const context = text.substring(start, end);
        
        // Check if "RV Fuel" appears near this date
        if (!/rv fuel/i.test(context)) continue;
        
        // Find amounts in this context
        const amountMatches = context.match(/(\d+\.\d{2,3})/g);
        if (!amountMatches) continue;
        
        for (const amtStr of amountMatches) {
          const amount = parseFloat(amtStr);
          if (amount < 15 || amount > 150) continue; // Typical fuel range
          
          const key = `${formattedDate}-${amount.toFixed(2)}`;
          if (seenDates.has(key)) continue;
          seenDates.add(key);
          
          const exists = transactions.some(t => 
            t.date === formattedDate && Math.abs(t.amount - amount) < 0.02
          );
          
          if (!exists) {
            transactions.push({
              id: crypto.randomUUID(),
              date: formattedDate,
              description: `⛽ TSD RV Fuel`,
              amount,
              category: 'fuel',
              isBusiness: true,
              isIncome: false,
              selected: true,
              bankSource: 'Other'
            });
          }
        }
      }
    }
    
    console.log('TSD Fuel parsed:', transactions.length, 'transactions');
    return transactions;
  };

  // Parse PayPal statement
  const parsePayPalStatement = (text: string): BankTransaction[] => {
    const transactions: BankTransaction[] = [];
    
    // Extract year from statement period: "May 1, 2025 - May 31, 2025"
    const periodMatch = text.match(/(\w+)\s+\d{1,2},?\s*(\d{4})\s*[-–]\s*\w+\s+\d{1,2},?\s*(\d{4})/i);
    const year = periodMatch ? periodMatch[3] || periodMatch[2] : currentYear.toString();
    console.log('PayPal statement year:', year);
    
    // PayPal table format patterns
    // Pattern 1: Standard table row - DATE | DESCRIPTION | CURRENCY | AMOUNT | FEES | TOTAL
    const tableRowPattern = /(\d{2}\/\d{2}\/\d{4})\s*\|?\s*([^|]+?(?:PayPal Credit|Checking|Savings)[^|]*?)\s*\|?\s*USD\s*\|?\s*(-?[\d,]+\.\d{2})\s*\|?\s*(-?[\d,]+\.\d{2})\s*\|?\s*(-?[\d,]+\.\d{2})/gi;
    
    // Pattern 2: Text-based format - MM/DD/YYYY Description ... -Amount
    const textPattern = /(\d{2})\/(\d{2})\/(\d{4})\s+(.+?)\s+(?:USD\s+)?(-?[\d,]+\.\d{2})\s+(?:-?[\d,]+\.\d{2})\s+(-?[\d,]+\.\d{2})/gi;
    
    // Pattern 3: Simpler format - MM/DD/YYYY | Description | USD | Amount
    const simplePattern = /\|\s*(\d{2})\/(\d{2})\/(\d{4})\s*\|\s*([^|]+)\s*\|\s*USD\s*\|\s*(-?[\d,]+\.\d{2})/gi;
    
    // Try all patterns
    let match;
    
    // Pattern 1: Full table row
    while ((match = tableRowPattern.exec(text)) !== null) {
      const dateStr = match[1]; // MM/DD/YYYY
      const description = match[2].trim();
      const amount = Math.abs(parseFloat(match[3].replace(/,/g, '')));
      const total = parseFloat(match[5].replace(/,/g, ''));
      
      // Parse date to YYYY-MM-DD
      const [month, day, yr] = dateStr.split('/');
      const formattedDate = `${yr}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Skip transfers and credit payments to BML (these are PayPal Credit payments)
      if (description.toLowerCase().includes('transfer to bml') ||
          description.toLowerCase().includes('buyer credit payment')) {
        continue;
      }
      
      // Extract clean description (vendor name)
      let cleanDesc = description
        .replace(/PreApproved Payment Bill User Payment:/gi, '')
        .replace(/Express Checkout Payment:/gi, '')
        .replace(/Subscription Payment:/gi, '')
        .replace(/General Payment:/gi, '')
        .replace(/Payment Refund:/gi, 'REFUND: ')
        .replace(/PayPal Credit.*$/gi, '')
        .replace(/CAPITAL ONE.*$/gi, '')
        .replace(/\d+\.\d{2}\s*USD/gi, '')
        .replace(/ID:\s*[A-Z0-9]+/gi, '')
        .trim();
      
      const { category, isBusiness } = categorizeTransaction(cleanDesc, amount);
      const isIncome = total > 0;
      
      // Check for duplicates
      const exists = transactions.some(t => 
        t.date === formattedDate && 
        Math.abs(t.amount - amount) < 0.02
      );
      
      if (!exists && cleanDesc.length > 1) {
        transactions.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          description: cleanDesc,
          amount,
          category,
          isBusiness,
          isIncome,
          selected: isBusiness === true && !isIncome,
          bankSource: 'PayPal'
        });
      }
    }
    
    // If no transactions found, try simpler parsing
    if (transactions.length === 0) {
      // Parse line by line for PayPal format
      const lines = text.split('\n');
      
      for (const line of lines) {
        // Match: MM/DD/YYYY vendor_name ... amount
        const lineMatch = line.match(/(\d{2})\/(\d{2})\/(\d{4})\s+([A-Za-z].+?)\s+(?:USD\s+)?(-?\d+\.\d{2})/);
        
        if (lineMatch) {
          const [, month, day, yr, rawDesc, amtStr] = lineMatch;
          const formattedDate = `${yr}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          const amount = Math.abs(parseFloat(amtStr));
          
          // Clean up description
          let cleanDesc = rawDesc
            .replace(/PreApproved Payment Bill User Payment:/gi, '')
            .replace(/Express Checkout Payment:/gi, '')
            .replace(/Subscription Payment:/gi, '')
            .replace(/PayPal Credit/gi, '')
            .trim();
          
          // Skip if it looks like a transfer
          if (cleanDesc.toLowerCase().includes('transfer to bml') ||
              cleanDesc.toLowerCase().includes('buyer credit')) {
            continue;
          }
          
          const { category, isBusiness } = categorizeTransaction(cleanDesc, amount);
          const isIncome = amtStr.startsWith('-') === false && !rawDesc.toLowerCase().includes('payment');
          
          const exists = transactions.some(t => 
            t.date === formattedDate && 
            Math.abs(t.amount - amount) < 0.02
          );
          
          if (!exists && cleanDesc.length > 2) {
            transactions.push({
              id: crypto.randomUUID(),
              date: formattedDate,
              description: cleanDesc,
              amount,
              category,
              isBusiness,
              isIncome,
              selected: isBusiness === true && !isIncome,
              bankSource: 'PayPal'
            });
          }
        }
      }
    }
    
    console.log('PayPal parsed transactions:', transactions.length);
    return transactions;
  };

  // Detect bank source from statement text
  const detectBankSource = (text: string): BankTransaction['bankSource'] => {
    const textLower = text.toLowerCase();
    
    // SmartFunds Card detection (fuel card)
    if (textLower.includes('smartfunds') || 
        textLower.includes('card activity report') ||
        (textLower.includes('wdrw') && textLower.includes('load') && textLower.includes('ulsd'))) {
      return 'Other'; // SmartFunds card
    }
    
    // PayPal detection
    if (textLower.includes('paypal account') || 
        textLower.includes('paypal balance') ||
        textLower.includes('paypal.com') ||
        (textLower.includes('paypal') && textLower.includes('account activity'))) {
      return 'PayPal';
    }
    if (textLower.includes('green phoenix')) {
      return 'GPA';
    }
    if (textLower.includes('navy federal') || 
        textLower.includes('navyfederal') ||
        textLower.includes('insured by ncua') ||
        /\|\s*\d{2}-\d{2}\s*\|.*?\|\s*[\d,]+\.\d{2}-\s*\|/.test(text)) {
      return 'NFCU';
    }
    if (textLower.includes('credit limit') ||
        textLower.includes('minimum payment due') ||
        textLower.includes('available credit')) {
      return 'Capital One CC';
    }
    if (textLower.includes('capital one') || textLower.includes('capitalone.com')) {
      return 'Capital One';
    }
    return 'Other';
  };

  // Parse text (shared between PDF and manual paste)
  const parseText = (text: string): BankTransaction[] => {
    let parsed: BankTransaction[] = [];
    const textLower = text.toLowerCase();
    
    // Detect bank source
    const bankSource = detectBankSource(text);
    console.log('Detected bank source:', bankSource);
    
    // Detect statement type (case-insensitive)
    const isNFCU = textLower.includes('navy federal') || 
                   textLower.includes('navyfederal.org') ||
                   textLower.includes('navyfederal') ||
                   (textLower.includes('nfcu') && !textLower.includes('from nfcu')) ||
                   textLower.includes('green phoenix') ||  // GPA business account at NFCU
                   textLower.includes('insured by ncua') ||
                   // NFCU format detection: MM-DD dates with trailing minus amounts
                   /\|\s*\d{2}-\d{2}\s*\|.*?\|\s*[\d,]+\.\d{2}-\s*\|/.test(text);
    
    const isCreditCard = textLower.includes('credit limit') ||
                         textLower.includes('minimum payment due') ||
                         textLower.includes('available credit') ||
                         (textLower.includes('cash advance') && textLower.includes('apr')) ||
                         (textLower.includes('interest charged') && textLower.includes('apr'));
    
    const isCapitalOneChecking = textLower.includes('capital one 360') ||
                                  textLower.includes('total control checking') ||
                                  textLower.includes('360 savings') ||
                                  (textLower.includes('capitalone.com') && !isCreditCard) ||
                                  textLower.includes('thanks for saving with capital one');
    
    const isPayPal = textLower.includes('paypal account') || 
                     textLower.includes('paypal balance') ||
                     textLower.includes('paypal.com') ||
                     (textLower.includes('paypal') && textLower.includes('account activity'));
    
    const isSmartFunds = textLower.includes('smartfunds') || 
                         textLower.includes('card activity report') ||
                         (textLower.includes('wdrw') && textLower.includes('load') && textLower.includes('ulsd'));
    
    // Love's Travel Stops Invoice (GPA fuel card)
    const isLovesInvoice = textLower.includes("love's travel stops") ||
                           textLower.includes('loves travel stops') ||
                           (textLower.includes('invoice') && textLower.includes('fuel tot') && textLower.includes('truck id'));
    
    // Love's Individual Receipts (detailed with odometer, gallons, etc.)
    const isLovesReceipt = (textLower.includes("love's") || textLower.includes('loves')) &&
                           textLower.includes('store') &&
                           textLower.includes('gallons') &&
                           (textLower.includes('tkt') || textLower.includes('ticket') || textLower.includes('total sale'));
    
    // TSD Logistics Agent Settlement Summary (RV Fuel deductions)
    const isTSDFuel = (textLower.includes('tsd logistics') || 
                       textLower.includes('tsd fuel') ||
                       textLower.includes('agent settlement summary')) && 
                      (textLower.includes('rv fuel') || 
                       textLower.includes('rvds') ||
                       textLower.includes('reimbursement for discount'));
    
    console.log('Statement detection:', { isSmartFunds, isLovesInvoice, isTSDFuel, isPayPal, isNFCU, isCreditCard, isCapitalOneChecking });
    
    // Try SmartFunds parser first if detected (fuel card)
    if (isSmartFunds) {
      console.log('>>> Detected SmartFunds Card Activity Report <<<');
      parsed = parseSmartFundsReport(text);
      console.log('SmartFunds parsed:', parsed.length, 'transactions');
      if (parsed.length > 0) {
        return parsed.map(t => ({ ...t, bankSource }));
      }
    }
    
    // Try Love's Travel Stops Invoice (GPA fuel card)
    if (isLovesInvoice) {
      console.log(">>> Detected Love's Travel Stops Invoice <<<");
      parsed = parseLovesInvoice(text);
      console.log('Loves Invoice parsed:', parsed.length, 'transactions');
      if (parsed.length > 0) {
        return parsed.map(t => ({ ...t, bankSource }));
      }
    }
    
    // Try Love's Individual Receipts (detailed receipts)
    if (isLovesReceipt) {
      console.log(">>> Detected Love's Receipt(s) <<<");
      parsed = parseLovesReceipts(text);
      console.log('Loves Receipts parsed:', parsed.length, 'transactions');
      if (parsed.length > 0) {
        return parsed.map(t => ({ ...t, bankSource }));
      }
    }
    
    // Try TSD Fuel Card
    if (isTSDFuel) {
      console.log('>>> Detected TSD Fuel Card Statement <<<');
      parsed = parseTSDFuelCard(text);
      console.log('TSD Fuel parsed:', parsed.length, 'transactions');
      if (parsed.length > 0) {
        return parsed.map(t => ({ ...t, bankSource }));
      }
    }
    
    // Try PayPal parser if detected
    if (isPayPal && parsed.length < 3) {
      console.log('Detected PayPal statement');
      parsed = parsePayPalStatement(text);
      console.log('PayPal parsed:', parsed.length, 'transactions');
    }
    
    // Try Capital One 360 Checking first if detected
    if (isCapitalOneChecking && parsed.length < 3) {
      console.log('Detected Capital One 360 Checking statement');
      parsed = parseCapitalOneStatement(text);
      console.log('Capital One Checking parsed:', parsed.length, 'transactions');
    }
    
    // Try NFCU parser
    if (isNFCU && parsed.length < 3) {
      console.log('Trying NFCU checking/savings statement');
      const nfcuParsed = parseNFCUStatement(text);
      if (nfcuParsed.length > parsed.length) {
        parsed = nfcuParsed;
      }
    }
    
    // Try Credit Card parser if detected or if other parsers fail
    if (isCreditCard || parsed.length < 3) {
      console.log('Trying Capital One Credit Card format');
      const ccParsed = parseCapitalOneCreditCard(text);
      if (ccParsed.length > parsed.length) {
        parsed = ccParsed;
      }
    }
    
    // Try Capital One checking linearized format
    if (parsed.length < 3) {
      console.log('Trying Capital One checking linearized format');
      const linearParsed = parseLinearizedStatement(text);
      if (linearParsed.length > parsed.length) {
        parsed = linearParsed;
      }
    }
    
    // Try Capital One checking line-by-line as final fallback
    if (parsed.length < 3) {
      console.log('Trying Capital One checking line-by-line format (fallback)');
      const lineParsed = parseCapitalOneStatement(text);
      if (lineParsed.length > parsed.length) {
        parsed = lineParsed;
      }
    }
    
    // Add bank source to all parsed transactions
    parsed = parsed.map(t => ({ ...t, bankSource }));
    
    // Cross-source duplicate detection for fuel purchases
    // Same date + similar amount (within $1) = likely duplicate from different source
    // (Love's receipt, Love's GPA invoice, TSD settlement could all have same purchase)
    parsed = parsed.filter((trans, index, self) => {
      // Keep non-fuel transactions
      if (trans.category !== 'fuel') return true;
      
      // Check if this is a duplicate of an earlier transaction in the list
      const isDuplicateFuel = self.slice(0, index).some(other => 
        other.category === 'fuel' &&
        other.date === trans.date &&
        Math.abs(other.amount - trans.amount) < 1.00 // Within $1
      );
      
      if (isDuplicateFuel) {
        console.log(`Skipping duplicate fuel: ${trans.date} $${trans.amount} - "${trans.description}"`);
      }
      
      return !isDuplicateFuel;
    });
    
    // Also check against existing database entries for fuel duplicates
    parsed = parsed.filter(trans => {
      if (trans.category !== 'fuel') return true;
      
      const existsInDB = expenses.some(exp => 
        exp.category === 'fuel' &&
        exp.date === trans.date &&
        Math.abs((exp.amount || 0) - trans.amount) < 1.00
      );
      
      if (existsInDB) {
        console.log(`Skipping fuel already in DB: ${trans.date} $${trans.amount}`);
      }
      
      return !existsInDB;
    });
    
    // Auto-detect payment company for income transactions based on description
    parsed = parsed.map(t => {
      if (t.isIncome) {
        const descUpper = t.description.toUpperCase();
        // Try to detect which company paid based on description
        if (descUpper.includes('BENNETT')) t.paymentCompany = 'Bennett';
        else if (descUpper.includes('AUTO DRIVEAWAY') || descUpper.includes('AUTODRIVEAWAY')) t.paymentCompany = 'Auto Driveaway';
        else if (descUpper.includes('QUALITY')) t.paymentCompany = 'Quality Driveaway';
        else if (descUpper.includes('COLDIRON')) t.paymentCompany = 'Coldiron';
        else if (descUpper.includes('DYNAMIC')) t.paymentCompany = 'Dynamic';
        else if (descUpper.includes('HORIZON')) t.paymentCompany = 'Horizon';
        else if (descUpper.includes('LAKEFRONT')) t.paymentCompany = 'Lakefront';
        else if (descUpper.includes('UNITED ROAD')) t.paymentCompany = 'United Road';
        else if (descUpper.includes('PASSTIME')) t.paymentCompany = 'Passtime';
        else if (descUpper.includes('GREEN PHOENIX') || descUpper.includes('GPA')) t.paymentCompany = 'GPA Transfer';
        // Select income by default if company is detected
        if (t.paymentCompany) t.selected = true;
      }
      return t;
    });
    
    // Detect suspicious income (personal transfers mistakenly flagged as income)
    parsed = parsed.map(t => {
      if (t.isIncome) {
        const suspicious = detectSuspiciousIncome(t.description, t.amount);
        if (suspicious.isSuspicious) {
          t.isSuspiciousIncome = true;
          t.suspiciousReason = suspicious.reason;
          t.selected = false; // Don't auto-select suspicious income
          t.paymentCompany = undefined; // Clear any auto-detected company
        }
      }
      return t;
    });
    
    return parsed;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let allTransactions: BankTransaction[] = [];
      
      for (const file of acceptedFiles) {
        console.log(`Processing file: ${file.name}`);
        const text = await extractTextFromPDF(file);
        setStatementText(prev => prev + '\n\n--- ' + file.name + ' ---\n\n' + text);
        
        const parsed = parseText(text);
        console.log(`Parsed ${parsed.length} transactions from ${file.name}`);
        
        // Add transactions, avoiding duplicates
        for (const trans of parsed) {
          const exists = allTransactions.some(t => 
            t.date === trans.date && 
            Math.abs(t.amount - trans.amount) < 0.01 &&
            t.description === trans.description
          );
          if (!exists) {
            allTransactions.push(trans);
          }
        }
      }
      
      if (allTransactions.length === 0) {
        setError('No transactions found in PDF(s). Try copying and pasting the statement text manually.');
      } else {
        // Sort by date
        allTransactions.sort((a, b) => a.date.localeCompare(b.date));
        setTransactions(prev => {
          // Merge with existing, avoiding duplicates
          const merged = [...prev];
          for (const trans of allTransactions) {
            const exists = merged.some(t => 
              t.date === trans.date && 
              Math.abs(t.amount - trans.amount) < 0.01
            );
            if (!exists) {
              merged.push(trans);
            }
          }
          return merged.sort((a, b) => a.date.localeCompare(b.date));
        });
        console.log(`Total: ${allTransactions.length} transactions from ${acceptedFiles.length} file(s)`);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try copying and pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true // Allow multiple files!
  });

  // Main parse function (for manual text paste)
  const handleParse = () => {
    if (!statementText.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const parsed = parseText(statementText);
      
      if (parsed.length === 0) {
        setError('No transactions found. Make sure you copied the full statement including dates and amounts.');
      } else {
        // Sort by date and merge with existing
        parsed.sort((a, b) => a.date.localeCompare(b.date));
        setTransactions(prev => {
          const merged = [...prev];
          for (const trans of parsed) {
            const exists = merged.some(t => 
              t.date === trans.date && 
              Math.abs(t.amount - trans.amount) < 0.01
            );
            if (!exists) {
              merged.push(trans);
            }
          }
          return merged.sort((a, b) => a.date.localeCompare(b.date));
        });
        console.log(`Parsed ${parsed.length} transactions`);
      }
    } catch (e: any) {
      setError(`Parse error: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle business/personal status
  const toggleBusiness = (id: string) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, isBusiness: !t.isBusiness, selected: !t.isBusiness } : t
    ));
  };

  // Toggle selection
  const toggleSelection = (id: string) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, selected: !t.selected } : t
    ));
  };

  // Remove transaction
  const removeTransaction = (id: string) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
  };

  // Set payment company for income transaction
  const setPaymentCompany = (id: string, company: string) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, paymentCompany: company, selected: true } : t
    ));
  };

  // Get bank badge color
  const getBankBadge = (bank?: string): { label: string; color: string } => {
    switch (bank) {
      case 'NFCU': return { label: 'NFCU', color: 'blue' };
      case 'Capital One': return { label: 'Cap1', color: 'red' };
      case 'Capital One CC': return { label: 'Cap1 CC', color: 'rose' };
      case 'GPA': return { label: 'GPA', color: 'purple' };
      default: return { label: 'Bank', color: 'gray' };
    }
  };

  // Get category display info
  const getCategoryInfo = (category: string): { label: string; icon: string; color: string } => {
    const categories: Record<string, { label: string; icon: string; color: string }> = {
      fuel: { label: 'Fuel', icon: '⛽', color: 'orange' },
      otr_misc: { label: 'OTR Misc', icon: '🚛', color: 'cyan' },
      insurance: { label: 'Insurance', icon: '🛡️', color: 'blue' },
      income: { label: 'Income', icon: '💰', color: 'green' },
      maintenance: { label: 'Maintenance', icon: '🔧', color: 'purple' },
      tolls: { label: 'Tolls', icon: '🛣️', color: 'cyan' },
      permits: { label: 'Permits/Scales', icon: '📋', color: 'indigo' },
      phone: { label: 'Phone', icon: '📱', color: 'pink' },
      meals: { label: 'Meals', icon: '🍔', color: 'yellow' },
      lodging: { label: 'Lodging', icon: '🏨', color: 'teal' },
      parking: { label: 'Parking', icon: '🅿️', color: 'slate' },
      streaming: { label: 'Streaming', icon: '📺', color: 'red' },
      rent: { label: 'Rent/Housing', icon: '🏠', color: 'stone' },
      investment: { label: 'Investment', icon: '📈', color: 'emerald' },
      food_delivery: { label: 'Food Delivery', icon: '🚗', color: 'rose' },
      utilities: { label: 'Utilities', icon: '💡', color: 'amber' },
      loan: { label: 'Loan', icon: '💳', color: 'violet' },
      transfer: { label: 'Transfer', icon: '↔️', color: 'gray' },
      payment: { label: 'CC Payment', icon: '💸', color: 'lime' },
      interest: { label: 'Interest', icon: '📊', color: 'red' },
      unknown: { label: 'Unknown', icon: '❓', color: 'gray' },
    };
    return categories[category] || categories.unknown;
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    if (filterType === 'income' && !t.isIncome) return false;
    if (filterType === 'business' && (t.isBusiness !== true || t.isIncome)) return false;
    if (filterType === 'personal' && (t.isBusiness !== false || t.isIncome)) return false;
    if (filterType === 'review' && (t.isBusiness !== null || t.isIncome)) return false;
    if (filterType === 'on_trip' && (t.isIncome || !isOnTrip(t.date))) return false;
    if (filterType === 'away' && (t.isIncome || isOnTrip(t.date) || !isLocationAwayFromHome(t.location).isAway || isLocationAwayFromHome(t.location).state === HOME_STATE)) return false;
    if (filterType === 'at_home' && (t.isIncome || isOnTrip(t.date) || isLocationAwayFromHome(t.location).isAway)) return false;
    if (filterType === 'suspicious' && !t.isSuspiciousIncome) return false;
    if (filterYear !== 'all' && !t.date.startsWith(filterYear.toString())) return false;
    return true;
  });

  // Get unique categories from transactions
  const uniqueCategories = [...new Set(transactions.map(t => t.category))];

  // Calculate summaries
  const summary = {
    totalIncome: transactions.filter(t => t.isIncome && t.selected && t.paymentCompany).reduce((sum, t) => sum + t.amount, 0),
    incomeCount: transactions.filter(t => t.isIncome).length,
    incomeReadyCount: transactions.filter(t => t.isIncome && t.paymentCompany).length,
    totalExpenses: transactions.filter(t => !t.isIncome && t.selected && t.isBusiness).reduce((sum, t) => sum + t.amount, 0),
    businessCount: transactions.filter(t => !t.isIncome && t.isBusiness === true).length,
    personalCount: transactions.filter(t => !t.isIncome && t.isBusiness === false).length,
    reviewCount: transactions.filter(t => !t.isIncome && t.isBusiness === null).length,
    selectedCount: transactions.filter(t => t.selected && (!t.isIncome || t.paymentCompany)).length,
    // OTR stats - cross-referenced with settlements AND location
    onTripCount: transactions.filter(t => !t.isIncome && isOnTrip(t.date)).length,
    onTripAmount: transactions.filter(t => !t.isIncome && isOnTrip(t.date)).reduce((sum, t) => sum + t.amount, 0),
    // Away from home (location-based detection)
    awayCount: transactions.filter(t => !t.isIncome && !isOnTrip(t.date) && isLocationAwayFromHome(t.location).isAway && isLocationAwayFromHome(t.location).state !== HOME_STATE).length,
    awayAmount: transactions.filter(t => !t.isIncome && !isOnTrip(t.date) && isLocationAwayFromHome(t.location).isAway && isLocationAwayFromHome(t.location).state !== HOME_STATE).reduce((sum, t) => sum + t.amount, 0),
    atHomeCount: transactions.filter(t => !t.isIncome && !isOnTrip(t.date) && !isLocationAwayFromHome(t.location).isAway).length,
    atHomeAmount: transactions.filter(t => !t.isIncome && !isOnTrip(t.date) && !isLocationAwayFromHome(t.location).isAway).reduce((sum, t) => sum + t.amount, 0),
    // Suspicious income warnings
    suspiciousIncomeCount: transactions.filter(t => t.isSuspiciousIncome).length,
    suspiciousIncomeAmount: transactions.filter(t => t.isSuspiciousIncome).reduce((sum, t) => sum + t.amount, 0),
  };

  // Import selected transactions
  const handleImport = async () => {
    const selected = filteredTransactions.filter(t => t.selected);
    if (selected.length === 0) return;
    
    setIsLoading(true);
    let success = 0;
    let errors = 0;
    let skipped = 0;
    let incomeImported = 0;
    
    for (const trans of selected) {
      // Skip duplicates
      if (isDuplicate(trans)) {
        console.log('Skipping duplicate:', trans.date, trans.amount, trans.description.slice(0, 30));
        skipped++;
        continue;
      }
      
      try {
        // Handle INCOME (deposits) - store as settlements
        if (trans.isIncome) {
          const company = trans.paymentCompany || 'Unknown';
          const bank = trans.bankSource || 'Unknown';
          
          // Create a detailed record for income tracking
          const incomeText = `Bank Deposit Record\n\n` +
            `Date: ${trans.date}\n` +
            `Amount: $${trans.amount.toFixed(2)}\n` +
            `Bank: ${bank}\n` +
            `Company: ${company}\n` +
            `Description: ${trans.description}\n\n` +
            `---\n` +
            `Imported from ${bank} bank statement on ${new Date().toISOString().split('T')[0]}\n` +
            `Original transaction: ${trans.description}`;
          const incomeBlob = new Blob([incomeText], { type: 'text/plain' });
          const filename = `income_${bank}_${trans.date}_${company.replace(/\s+/g, '_')}_$${trans.amount.toFixed(0)}.txt`;
          
          await addExpense({
            category: 'settlements' as any, // Income goes to settlements category
            description: `💰 ${company} deposit via ${bank}: ${trans.description.substring(0, 50)}`,
            amount: trans.amount,
            date: trans.date,
            file: incomeBlob,
            filename: filename,
          });
          incomeImported++;
          success++;
          continue;
        }
        
        // Handle EXPENSES
        // Map bank categories to expense categories
        const categoryMap: Record<string, string> = {
          fuel: 'fuel',
          otr_misc: 'meals', // Small truck stop purchases = OTR meals/misc
          insurance: 'miscellaneous',
          maintenance: 'miscellaneous',
          tolls: 'travel',
          permits: 'miscellaneous',
          phone: 'office',
          meals: 'meals',
          lodging: 'travel',
          parking: 'travel',
        };
        
        const expenseCategory = categoryMap[trans.category] || 'miscellaneous';
        const bank = trans.bankSource || 'Unknown';
        
        // Create a text file as placeholder receipt (bank statement imports don't have images)
        const receiptText = `Bank Statement Import\n\nDate: ${trans.date}\nDescription: ${trans.description}\nAmount: $${trans.amount.toFixed(2)}\nCategory: ${trans.category}\nBank: ${bank}\n\nImported from ${bank} bank statement on ${new Date().toISOString().split('T')[0]}`;
        const receiptBlob = new Blob([receiptText], { type: 'text/plain' });
        const filename = `bank_${bank}_${trans.date}_${trans.amount.toFixed(2).replace('.', '_')}.txt`;
        
        await addExpense({
          category: expenseCategory as any,
          description: `${getCategoryInfo(trans.category).icon} [${bank}] ${trans.description}`,
          amount: trans.amount,
          date: trans.date,
          file: receiptBlob,
          filename: filename,
        });
        success++;
      } catch (e) {
        console.error('Failed to import transaction:', e);
        errors++;
      }
    }
    
    setImportStatus({ success, errors, skipped, incomeImported } as any);
    // Remove imported transactions from list
    const importedIds = selected.map(t => t.id);
    setTransactions(prev => prev.filter(t => !importedIds.includes(t.id)));
    setIsLoading(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Landmark className="w-8 h-8 text-emerald-400" />
          Import Bank Statement
        </h1>
        <p className="text-shuttle-sky mt-1">
          Forensic expense tracking - reconstruct business expenses from bank records
        </p>
      </div>

      {/* Supported Banks */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/50">
          ✓ Capital One Checking
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-rose-500/20 text-rose-400 border border-rose-500/50">
          ✓ Capital One Credit Card
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
          ✓ Navy Federal (NFCU)
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/50">
          ✓ GPA Business
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/50">
          + Other banks
        </span>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-emerald-400 bg-emerald-500/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
        {isLoading ? (
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <p className="text-white text-lg">Processing PDF(s)...</p>
          </div>
        ) : isDragActive ? (
          <p className="text-white text-lg">Drop the statement PDF(s) here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop bank statement PDFs here
            </p>
            <p className="text-white/60 text-sm">
              Supports multiple files • Capital One, NFCU, GPA
            </p>
          </>
        )}
      </div>

      {/* Or Paste Area */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          📋 Or Paste Statement Text
        </h3>
        <p className="text-white/60 text-sm mb-3">
          Copy transaction rows from your bank statement and paste below.
        </p>
        <textarea
          value={statementText}
          onChange={(e) => setStatementText(e.target.value)}
          placeholder="| Jan 2 | Debit Card Purchase - PILOT TRAVEL CTR #123 | | - $85.50 | $1,234.56 |"
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none font-mono text-sm"
        />
        <div className="flex gap-3 mt-3">
          <button
            onClick={handleParse}
            disabled={!statementText.trim() || isLoading}
            className="px-6 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Parse & Categorize
          </button>
          <button
            onClick={() => { setStatementText(''); setTransactions([]); setError(null); setImportStatus(null); }}
            disabled={!statementText.trim() && transactions.length === 0}
            className="px-6 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.success > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.success > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.success > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.success > 0 && `Imported ${importStatus.success} transaction(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
              {importStatus.success === 0 && importStatus.skipped > 0 && 'No new transactions imported.'}
            </p>
            {importStatus.incomeImported && importStatus.incomeImported > 0 && (
              <p className="text-green-300 text-sm mt-1">
                💰 {importStatus.incomeImported} income deposit(s) added to Settlements!
              </p>
            )}
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {transactions.length > 0 && (
        <div className="space-y-4">
          {/* Main summary row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-green-500/20 rounded-xl p-4 border border-green-500/30">
              <div className="flex items-center gap-2 text-green-400 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Income</span>
              </div>
              <p className="text-2xl font-bold text-green-400">
                ${summary.totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-orange-500/20 rounded-xl p-4 border border-orange-500/30">
              <div className="flex items-center gap-2 text-orange-400 mb-1">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xs font-medium">Business Expenses</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">
                ${summary.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
              <div className="text-emerald-400 text-xs font-medium mb-1">✅ Business</div>
              <p className="text-2xl font-bold text-emerald-400">{summary.businessCount}</p>
            </div>
            <div className="bg-red-500/20 rounded-xl p-4 border border-red-500/30">
              <div className="text-red-400 text-xs font-medium mb-1">❌ Personal</div>
              <p className="text-2xl font-bold text-red-400">{summary.personalCount}</p>
            </div>
            <div className="bg-yellow-500/20 rounded-xl p-4 border border-yellow-500/30">
              <div className="text-yellow-400 text-xs font-medium mb-1">⚠️ Review</div>
              <p className="text-2xl font-bold text-yellow-400">{summary.reviewCount}</p>
            </div>
          </div>
          
          {/* OTR Trip Match row - shows which transactions happened while on the road */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
              <div className="flex items-center gap-2 text-emerald-400 mb-1">
                <span className="text-lg">🚚</span>
                <span className="text-sm font-medium">OTR (Settlement Match)</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">{summary.onTripCount}</p>
              <p className="text-xs text-emerald-400/70">${summary.onTripAmount.toFixed(2)} in expenses</p>
              <p className="text-xs text-white/40 mt-1">Matched with {tripRanges.length} trips</p>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <span className="text-lg">📍</span>
                <span className="text-sm font-medium">Away from TX</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{summary.awayCount}</p>
              <p className="text-xs text-amber-400/70">${summary.awayAmount.toFixed(2)} in expenses</p>
              <p className="text-xs text-white/40 mt-1">Location outside Texas = likely OTR</p>
            </div>
            <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
              <div className="flex items-center gap-2 text-blue-400 mb-1">
                <span className="text-lg">🏠</span>
                <span className="text-sm font-medium">Home Area</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">{summary.atHomeCount}</p>
              <p className="text-xs text-blue-400/70">${summary.atHomeAmount.toFixed(2)} in expenses</p>
              <p className="text-xs text-white/40 mt-1">Review for personal vs business</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {transactions.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-white/70 text-sm">Filter:</span>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Types</option>
            <option value="income">💰 Income Only ({summary.incomeCount})</option>
            <option value="business">✅ Business Only</option>
            <option value="personal">❌ Personal Only</option>
            <option value="review">⚠️ Needs Review</option>
            <option value="on_trip">🚚 While OTR ({summary.onTripCount})</option>
            <option value="away">📍 Away from TX ({summary.awayCount})</option>
            <option value="at_home">🏠 While Home ({summary.atHomeCount})</option>
            {summary.suspiciousIncomeCount > 0 && (
              <option value="suspicious">🚨 Suspicious Income ({summary.suspiciousIncomeCount})</option>
            )}
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map(cat => (
              <option key={cat} value={cat}>{getCategoryInfo(cat).icon} {getCategoryInfo(cat).label}</option>
            ))}
          </select>
          
          <div className="ml-auto flex items-center gap-2">
            <span className="text-white/50 text-sm">{filteredTransactions.length} transactions</span>
            <button
              onClick={handleImport}
              disabled={summary.selectedCount === 0 || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg font-medium hover:from-emerald-400 hover:to-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Save className="w-4 h-4" />
              Import {summary.selectedCount} Selected
            </button>
          </div>
        </div>
      )}

      {/* Transactions List */}
      {filteredTransactions.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
            {filteredTransactions.map((trans) => {
              const catInfo = getCategoryInfo(trans.category);
              const isExisting = isDuplicate(trans);
              const bankBadge = getBankBadge(trans.bankSource);
              return (
                <div
                  key={trans.id}
                  className={`p-4 flex items-center gap-3 transition-colors ${
                    isExisting ? 'bg-orange-500/10 opacity-60' :
                    trans.isSuspiciousIncome ? 'bg-red-500/10 border-l-4 border-red-500' :
                    trans.isIncome ? 'bg-green-500/10' :
                    trans.isBusiness === false ? 'bg-red-500/5 opacity-60' :
                    trans.isBusiness === null ? 'bg-yellow-500/10' :
                    trans.selected ? 'bg-white/5' : 'bg-transparent'
                  }`}
                >
                  {/* Duplicate warning */}
                  {isExisting && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50" title="Already in database">
                      DUP
                    </span>
                  )}
                  
                  {/* Selection checkbox */}
                  <input
                    type="checkbox"
                    checked={trans.selected && !isExisting}
                    disabled={isExisting || (trans.isIncome && !trans.paymentCompany)}
                    onChange={() => toggleSelection(trans.id)}
                    className="w-5 h-5 rounded border-white/30 bg-white/10 text-emerald-500 focus:ring-emerald-500 disabled:opacity-50"
                    title={trans.isIncome && !trans.paymentCompany ? 'Select a company first' : ''}
                  />
                  
                  {/* Business/Personal toggle OR Income badge */}
                  {trans.isIncome ? (
                    trans.isSuspiciousIncome ? (
                      <span 
                        className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse"
                        title={trans.suspiciousReason || 'This looks like a personal transfer, not settlement income'}
                      >
                        ⚠️ NOT INC
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
                        💰 INC
                      </span>
                    )
                  ) : (
                    <button
                      onClick={() => toggleBusiness(trans.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        trans.isBusiness === true ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' :
                        trans.isBusiness === false ? 'bg-red-500/20 text-red-400 border border-red-500/50' :
                        'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                      }`}
                    >
                      {trans.isBusiness === true ? '✅ BIZ' : trans.isBusiness === false ? '❌ PERS' : '⚠️ ???'}
                    </button>
                  )}
                  
                  {/* Bank badge */}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${bankBadge.color}-500/20 text-${bankBadge.color}-400 border border-${bankBadge.color}-500/50 whitespace-nowrap`}>
                    🏦 {bankBadge.label}
                  </span>
                  
                  {/* Date + Trip Status */}
                  <div className="flex items-center gap-2">
                    <div className="w-24 flex items-center gap-1 text-white/60 text-sm">
                      <Calendar className="w-3 h-3" />
                      {trans.date}
                    </div>
                    {/* Trip indicator - shows if you were OTR on this date */}
                    {(() => {
                      const trip = findTripForDate(trans.date);
                      const locationCheck = isLocationAwayFromHome(trans.location);
                      
                      // Method 1: Settlement match (highest confidence)
                      if (trip) {
                        return (
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 whitespace-nowrap"
                            title={`On trip: ${trip.origin} → ${trip.destination} (${trip.company})`}
                          >
                            🚚 OTR
                          </span>
                        );
                      }
                      
                      // Method 2: Location away from home (TX) - strong indicator of OTR
                      if (locationCheck.isAway && locationCheck.state && locationCheck.state !== HOME_STATE) {
                        return (
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50 whitespace-nowrap"
                            title={`Location in ${locationCheck.state} - away from home (TX). Likely OTR!`}
                          >
                            📍 AWAY
                          </span>
                        );
                      }
                      
                      // Method 3: In Texas but not a home city
                      if (locationCheck.isAway && locationCheck.state === HOME_STATE) {
                        return (
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 whitespace-nowrap"
                            title="In TX but not your home area - check if OTR"
                          >
                            ❓ TX?
                          </span>
                        );
                      }
                      
                      // Default: Home area
                      return (
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50 whitespace-nowrap"
                          title="Not on a recorded trip this date"
                        >
                          🏠 HOME
                        </span>
                      );
                    })()}
                  </div>
                  
                  {/* Category badge (for expenses) or Company selector (for income) */}
                  {trans.isIncome ? (
                    <select
                      value={trans.paymentCompany || ''}
                      onChange={(e) => setPaymentCompany(trans.id, e.target.value)}
                      className={`px-2 py-1 rounded text-xs font-medium bg-white/10 border transition-colors ${
                        trans.paymentCompany 
                          ? 'border-green-500/50 text-green-400' 
                          : 'border-yellow-500/50 text-yellow-400'
                      } focus:ring-2 focus:ring-green-500`}
                    >
                      <option value="">⚠️ Select Company...</option>
                      {TRANSPORT_COMPANIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${catInfo.color}-500/20 text-${catInfo.color}-400 border border-${catInfo.color}-500/50 whitespace-nowrap`}>
                      {catInfo.icon} {catInfo.label}
                    </span>
                  )}
                  
                  {/* Description */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white truncate text-sm">{trans.description}</p>
                    {trans.location && (
                      <p className="text-white/40 text-xs mt-0.5">📍 {trans.location}</p>
                    )}
                    {trans.isSuspiciousIncome && (
                      <p className="text-red-400 text-xs mt-0.5 font-medium">
                        ⚠️ {trans.suspiciousReason || 'This looks like a personal transfer, NOT settlement income'}
                      </p>
                    )}
                  </div>
                  
                  {/* Amount */}
                  <div className={`text-right font-mono font-bold ${trans.isIncome ? 'text-green-400' : 'text-red-400'}`}>
                    {trans.isIncome ? '+' : '-'}${trans.amount.toFixed(2)}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    onClick={() => removeTransaction(trans.id)}
                    className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">🔍 How Forensic Import Works</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-400">✅ Auto-Detected as Business:</h4>
            <ul className="text-white/70 space-y-1">
              <li>• Fuel: Loves, Pilot, Flying J, TA, etc.</li>
              <li>• Insurance: Truck/commercial policies</li>
              <li>• Maintenance: AutoZone, NAPA, truck parts</li>
              <li>• Tolls: EZPass, SunPass, turnpikes</li>
              <li>• Income: SmartPay, EFS, carrier deposits</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-red-400">❌ Auto-Detected as Personal:</h4>
            <ul className="text-white/70 space-y-1">
              <li>• Streaming: Netflix, Spotify, etc.</li>
              <li>• Housing: Rent, apartments</li>
              <li>• Investments: Robinhood, brokerages</li>
              <li>• Transfers: Zelle, Cash App, PayPal</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-yellow-400/80 text-sm">
          ⚠️ Items marked "???" need your review - click to toggle business/personal!
        </p>
        <p className="mt-2 text-emerald-400/80 text-sm">
          🚚 <strong>Trip Match Active!</strong> Each transaction shows if you were OTR or HOME based on your settlement dates.
          Expenses while OTR are likely business deductible!
        </p>
      </div>
    </div>
  );
}

